import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { formatDateOnly, toDateOnlyString } from "../lib/dateOnly";
import { formatPickWindows, isPatientPickRequest, pickClinicLabel } from "../lib/pickWindows";
import {
  formatWallClockDate, formatWallClockTime, formatWallClockDateTime,
  parseWallClock, isWallClockPast,
} from "../lib/wallClock";
import { Phone, PlayCircle, RefreshCw, X, Clock, User, Building2, Briefcase, FileText, ChevronDown, Search, Filter, MapPin, CalendarDays, ChevronLeft, ChevronRight, CalendarCheck, History, Stethoscope, Mail, Edit2, Check, XCircle, FolderOpen, ExternalLink, AlertTriangle, Loader2, Send, Eye, EyeOff, Lock, Target } from "lucide-react";

// Mirrors backend crm_router.INTAKE_TYPE_VALUES (verbatim, en-dash in the fourth).
const INTAKE_TYPE_OPTIONS = ["New", "Continuing", "Readmit", "Returning Patient – New Case"];

// The one place the lock-in copy lives (Ticket 1). Shown under the disabled
// Next button and repeated as its hover title.
const NEXT_LOCKED_COPY = "Record an action to move to the next patient.";

// TICKET 1 (2026-09-05): the skip affordance was REMOVED from queue mode —
// a rep is locked into the served patient until they record an action. The
// `/api/crm/queue/skip` endpoint, the `crm_queue_skips` table and every
// historical row stay intact for reporting and for a possible future policy
// change; only the UI is gone, which is why these labels are gone too.
import { Icd10Panel } from "../components/Icd10Panel";
import { CollapsibleSection } from "../components/CollapsibleSection";
import { TextHistoryPanel } from "../components/TextHistoryPanel";
import { EsignPanel, EsignChip } from "../components/EsignPanel";
import { useWritesInFlight } from "../lib/cardWrites";
// P-5: the consent text has ONE home (Manual Referral Entry, pinned verbatim
// by test_the_consent_v03_strings_are_verbatim); the panel imports it.
import { SMS_CONSENT_CHECKBOX_TEXT, SMS_CONSENT_FULL_TEXT } from "./ManualReferralEntry";
import { pickPrefilledLocation } from "../lib/crmPatientModal";
import { cancelPromptText, cancelResultText } from "../lib/cancelPrompt";
import { liveRowsFor, cancelOutcome, rescheduleOrphanText, liveCacheGet, liveCacheSet, liveCacheDrop } from "../lib/liveVisits";
import { clearCardPrefetch } from "../lib/cardPrefetch";
import CardVisitsPanel from "../components/CardVisitsPanel";
import { toast as sonnerToast } from "sonner";
import { useMyLocationCodes } from "../lib/useMyLocationCodes";
// CRM statuses v2: the model from GET /crm/stages, only with STATUS_NAMES_V2 on.
import { useStagesV2, useStageModel, StageModelContext } from "../lib/stagesV2";
import StageTabsV2 from "../components/StageTabsV2";
import { appendBookingExtras, saturdayOpen } from "../lib/bookingLocations";
import { LOCATION_EDITABLE_FIELDS } from "../constants/locationEditableFields";
import {
  NETWORK_APPOINTMENT_TYPES_BY_CATEGORY,
  NETWORK_APPOINTMENT_TYPE_DEFAULT,
  isSupportedNetworkApptType,
} from "../constants/networkAppointmentTypes";

// Generate unique session ID for this browser tab (persists across refreshes)
const generateSessionId = () => {
  let sessionId = sessionStorage.getItem('crm_session_id');
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('crm_session_id', sessionId);
  }
  return sessionId;
};

// ── Date-only formatters ──────────────────────────────────────────────────
// 2026-08-20 hotfix: the previous `new Date(dateValue).toLocaleDateString`
// path parsed 'YYYY-MM-DD' as UTC midnight and formatted it in Pacific,
// shifting −1 day. Now delegates to `formatDateOnly` in lib/dateOnly.js,
// which does pure string reordering — no `new Date("YYYY-MM-DD")` anywhere.
// Kept as thin wrappers so the callsites' null semantics (N/A vs em-dash)
// stay explicit at each usage.
function formatDOI(dateValue) {
  return formatDateOnly(dateValue, { nullLabel: "N/A" });
}

// Item 7 (2026-09-20) — ONE injury-date value, two possible sources.
// WC referrals carry `date_of_injury`; PI referrals carry
// `date_of_accident`. The label follows whichever value survived the
// coalesce, so a PI card says what the date actually is.
function injuryDateLabel(p) {
  if (!p?.date_of_injury && p?.date_of_accident && p?.case_type === "PI") {
    return "Date of accident";
  }
  return "Date of Injury";
}

// The displayed value, and the warning label when there is nothing to
// display. Never "N/A" — a missing injury date is a gap to fix on the
// Intake tab, not a field that doesn't apply.
function injuryDateValue(p) {
  return p?.date_of_injury || p?.date_of_accident || null;
}

function formatDOB(dateValue) {
  return formatDateOnly(dateValue, { nullLabel: "—" });
}

// ── API error formatter ────────────────────────────────────────────────────────
// Normalizes any backend error into a renderable string.
// Handles FastAPI/Pydantic 422 (array of {type, loc, msg, input, url}),
// {detail: "..."} strings, and unknown shapes — never returns a non-string.
function formatApiError(err, fallback = "Action failed") {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        if (typeof d === "string") return d;
        const loc = Array.isArray(d?.loc) ? d.loc.filter((p) => p !== "body").join(".") : "";
        const msg = d?.msg || "Invalid value";
        return loc ? `${loc}: ${msg}` : msg;
      })
      .join("; ");
  }
  if (typeof detail === "object") {
    return detail.msg || detail.message || JSON.stringify(detail);
  }
  return String(detail);
}

// ── Stage colour mapping ──────────────────────────────────────────────────────
const STAGE_STYLES = {
  1:  { bg: "bg-emerald-100", text: "text-emerald-800", dot: "bg-emerald-500" },
  2:  { bg: "bg-blue-100",    text: "text-blue-800",    dot: "bg-blue-500"    },
  3:  { bg: "bg-indigo-100",  text: "text-indigo-800",  dot: "bg-indigo-500"  },
  4:  { bg: "bg-amber-100",   text: "text-amber-800",   dot: "bg-amber-500"   },
  5:  { bg: "bg-purple-100",  text: "text-purple-800",  dot: "bg-purple-500"  },
  6:  { bg: "bg-orange-100",  text: "text-orange-800",  dot: "bg-orange-500"  },
  7:  { bg: "bg-orange-100",  text: "text-orange-800",  dot: "bg-orange-400"  },
  8:  { bg: "bg-slate-100",   text: "text-slate-600",   dot: "bg-slate-400"   },
  9:  { bg: "bg-slate-100",   text: "text-slate-600",   dot: "bg-slate-400"   },
  10: { bg: "bg-slate-100",   text: "text-slate-600",   dot: "bg-slate-400"   },
  11: { bg: "bg-slate-100",   text: "text-slate-600",   dot: "bg-slate-400"   },
  12: { bg: "bg-slate-100",   text: "text-slate-600",   dot: "bg-slate-400"   },
  13: { bg: "bg-sky-100",     text: "text-sky-800",     dot: "bg-sky-500"     },
  14: { bg: "bg-teal-100",    text: "text-teal-800",    dot: "bg-teal-500"    },
  15: { bg: "bg-emerald-100", text: "text-emerald-800", dot: "bg-emerald-600" },
  16: { bg: "bg-fuchsia-100", text: "text-fuchsia-800", dot: "bg-fuchsia-500" },
};

const ALL_STAGES = [
  { id: 1,  name: "Ready to Schedule" },
  { id: 2,  name: "First Call / Left Message" },
  { id: 3,  name: "Second Call" },
  { id: 4,  name: "Third Call / No Response" },
  // Network-scheduling journey stages (2026-06) — conceptually they sit
  // between the call stages (2–4) and "Scheduled Initial Visit" (5).
  // 13–15 flip automatically from the scheduling flows; 16 is manual-only.
  { id: 13, name: "Proposed Times" },
  { id: 14, name: "Network Suggested" },
  { id: 15, name: "Network Confirmed" },
  { id: 16, name: "Needs Patient Confirmation" },
  { id: 5,  name: "Scheduled Initial Visit" },
  { id: 6,  name: "Call to Reschedule (No-Show)" },
  { id: 7,  name: "Call to Reschedule (Canceled)" },
  { id: 8,  name: "Closed: Completed Initial Visit" },
  { id: 9,  name: "Closed: No Response" },
  { id: 10, name: "Closed: Patient Decline Services" },
  { id: 11, name: "Closed: Canceled by Attorney" },
  { id: 12, name: "Closed: Continue Docs Sent" },
];

// Terminal-state stage IDs. Patients in these stages roll up into the
// "Closed" bucket on the queue header and out of the active funnel pills.
const CLOSED_STAGE_IDS = [8, 9, 10, 11, 12];
// Stages that appear as individual pills on the left of the strip.
const ACTIVE_PILL_STAGE_IDS = [1, 2, 3, 4, 13, 14, 15, 16, 5, 6, 7];

// ── Time-in-stage formatter ───────────────────────────────────────────────────
function formatTime(hours) {
  if (!hours && hours !== 0) return "—";
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  if (hours < 24) return `${Math.round(hours)}h`;
  const d = Math.floor(hours / 24);
  const h = Math.round(hours % 24);
  return h > 0 ? `${d}d ${h}h` : `${d}d`;
}

// ── Wall-clock appointment stamps ─────────────────────────────────────────
//
// The private `parseLocalDateTime` that used to live here is now
// `lib/wallClock.js` (2026-09-01). It was correct but PRIVATE, so the
// global patient search grew its own converting formatter and showed a
// 7:00 AM appointment as 2:00 AM to a Central-time viewer. One
// implementation, imported everywhere. The shared version also pins
// `timeZone: "UTC"` instead of stripping the offset, which additionally
// survives the spring-forward hour (02:30 local does not exist).


// ── Stage badge ───────────────────────────────────────────────────────────────
function StageBadge({ stageId, stageName, small }) {
  const v2 = useStageModel(); // CRM statuses v2: null with STATUS_NAMES_V2 off
  if (v2) {
    const vs = v2.styleFor(stageId);
    return (
      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-medium whitespace-nowrap text-xs ${vs.bg} ${vs.text}`}>
        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${vs.dot}`} />
        {v2.labelFor(stageId, stageName)}
      </span>
    );
  }
  const s = STAGE_STYLES[stageId] || STAGE_STYLES[2];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${small ? "text-xs" : "text-xs"} ${s.bg} ${s.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${s.dot}`} />
      {stageName}
    </span>
  );
}

// Intake-type pill — sourced from BQ weststar_documents.intake_type via the
// 5-second CRM sync. NULL / unknown values render nothing (historic rows have
// no intake_type; backfill is forward-only by design — not an error state).
const INTAKE_TYPE_STYLES = {
  "New":                          { bg: "bg-blue-100",   text: "text-blue-700",   dot: "bg-blue-500"   },
  "Continuing":                   { bg: "bg-slate-100",  text: "text-slate-700",  dot: "bg-slate-400"  },
  "Readmit":                      { bg: "bg-amber-100",  text: "text-amber-700",  dot: "bg-amber-500"  },
  "Returning Patient – New Case": { bg: "bg-purple-100", text: "text-purple-700", dot: "bg-purple-500" },
};
// WestStar/Network row badge (2026-06 master filter) — value comes ONLY
// from the server-side `location_class` field (services/location_classifier
// is the single classification source; the raw mapping never ships here).
const LOCATION_CLASS_STYLES = {
  weststar:   { chip: "bg-indigo-100 text-indigo-700 border border-indigo-200", dot: "bg-indigo-500", label: "WestStar" },
  network:    { chip: "bg-teal-100 text-teal-700 border border-teal-200",       dot: "bg-teal-500",   label: "Network" },
  // 2026-09-05: telehealth is its own routing bucket (server-computed
  // `queue_bucket`), so the row says so instead of reading "WestStar".
  telehealth: { chip: "bg-sky-100 text-sky-700 border border-sky-200",          dot: "bg-sky-500",    label: "Telehealth" },
  unassigned: { chip: "bg-slate-100 text-slate-500 border border-slate-200",    dot: "bg-slate-400",  label: "Unassigned" },
};

// The four master-filter settings. Both = Network + WestStar (and
// unassigned) — explicitly NOT telehealth, which a dedicated person works.
const LOCATION_FILTER_OPTIONS = [
  { key: "weststar", label: "WestStar" },
  { key: "network", label: "Network" },
  { key: "telehealth", label: "Telehealth" },
  { key: "both", label: "Both" },
];

// ── List-view sorting (2026-09) ────────────────────────────────────────────
// EVERY column sorts SERVER-SIDE. The list paginates at 200/page, so a
// client-side sort would order one page and lie about the extremes — the
// sort rides `sort_by`/`sort_dir` on /crm/patients, whose whitelist
// (crm_router.list_sort_columns) is the security boundary. Keys here MUST
// exist in that whitelist: an unknown key is a 422, not a silent fallback.
//
// Queue mode's serving order is the engine's and is untouched by any of
// this — this is the manager/exception surface only.
const SORT_COLUMNS = [
  { key: "patient", label: "Patient", firstDir: "asc", testId: "patient-header" },
  { key: "phone", label: "Phone", firstDir: "asc" },
  { key: "assigned_clinic", label: "Assigned Clinic", firstDir: "asc" },
  { key: "status", label: "Status", firstDir: "asc" },
  { key: "intake_type", label: "Intake Type", firstDir: "asc" },
  {
    key: "hours_in_stage", label: "Time in Stage", firstDir: "asc",
    testId: "time-in-stage-header",
    // Legacy indicator test ids — the Time in Stage pins predate the
    // generic header and still assert on these exact hooks.
    indicatorTestIds: { asc: "time-in-stage-sort-asc", desc: "time-in-stage-sort-desc" },
  },
  { key: "date_of_injury", label: "DOI", firstDir: "desc" },
  {
    key: "initial_eval", label: "Initial Eval", firstDir: "desc",
    // The tooltip is a claim about system behaviour, so it says exactly what
    // the code does: the book paths write the mirror, a reschedule
    // overwrites it, and `resync_patient_appointment_header` clears it when
    // no upcoming appointment remains. A past date on an undisposed patient
    // is real and stays visible.
    title: "Currently scheduled initial visit — moves on reschedule, clears when no appointment remains",
  },
  { key: "icd10", label: "ICD-10", firstDir: "asc" },
  { key: "created_at", label: "Date Added", firstDir: "desc", testId: "date-added-header" },
  { key: "therapy_type", label: "Therapy", firstDir: "asc" },
  { key: "payor", label: "Payor", firstDir: "asc" },
  { key: "referring_dr", label: "Referring Dr", firstDir: "asc" },
  { key: "notes", label: "Notes", firstDir: "asc" },
];

// Three-state cycle: unsorted → the column's leading direction → the other
// one → back to the queue's default priority order. Dates lead newest-first;
// text leads A→Z; Time in Stage leads ascending (freshest) per its ticket.
function nextSortState(current, col) {
  if (!current || current.by !== col.key) return { by: col.key, dir: col.firstDir };
  const other = col.firstDir === "asc" ? "desc" : "asc";
  if (current.dir === col.firstDir) return { by: col.key, dir: other };
  return null;
}

function SortableTh({ col, sort, onSort }) {
  const active = sort?.by === col.key ? sort.dir : null;
  const ids = col.indicatorTestIds || {};
  return (
    <th
      onClick={() => onSort(nextSortState(sort, col))}
      className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap cursor-pointer select-none hover:text-slate-700 transition-colors"
      data-testid={col.testId || `sort-header-${col.key}`}
      title={col.title || `Sort by ${col.label}`}
      aria-sort={active === "asc" ? "ascending" : active === "desc" ? "descending" : "none"}
    >
      <span className="inline-flex items-center gap-1">
        {col.label}
        {active === "asc" && (
          <span aria-hidden="true" data-testid={ids.asc || `sort-asc-${col.key}`}>▲</span>
        )}
        {active === "desc" && (
          <span aria-hidden="true" data-testid={ids.desc || `sort-desc-${col.key}`}>▼</span>
        )}
      </span>
    </th>
  );
}

// House display convention for the assigned clinic — "Name — City"
// (matches the patient modal). The city is the HUMAN discriminator for the
// bare "West Star" sites exactly as `assigned_location_code` is the machine
// one; the code stays the access boundary and is never shown here.
function assignedClinicLabel(p) {
  const name = (p.assigned_clinic || "").trim();
  const city = (p.assigned_clinic_city || "").trim();
  if (!name && !city) return null;
  if (!city) return name;
  if (!name) return city;
  return `${name} — ${city}`;
}


function LocationClassBadge({ cls, testId }) {
  const s = LOCATION_CLASS_STYLES[cls] || LOCATION_CLASS_STYLES.unassigned;
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${s.chip}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

function IntakeTypeBadge({ type }) {
  if (!type) return null;
  const s = INTAKE_TYPE_STYLES[type];
  if (!s) return null;  // unknown value — render nothing, don't guess a color
  return (
    <span
      data-testid={`intake-type-badge-${type.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-medium whitespace-nowrap text-xs ${s.bg} ${s.text}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${s.dot}`} />
      {type}
    </span>
  );
}

// "ICD-10 Review" — red badge that appears next to ICD-10 in both the list
// row and the QueueBanner header. Triggered server-side via `icd10_review`
// (a single bool that collapses low-confidence + non-null review note +
// codes-failed-to-parse). Defined in one place: services/icd10_service.py.
// Renamed from "Please Review" (item 3, 2026-09-20): a badge has to say
// WHAT is being reviewed.
function Icd10ReviewBadge({ show }) {
  if (!show) return null;
  return (
    <span
      data-testid="icd10-review-badge"
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full font-semibold text-[10px] bg-red-100 text-red-700 whitespace-nowrap"
      title="ICD-10 codes not yet confirmed. Open the ICD-10 panel to confirm."
    >
      <span className="w-1 h-1 rounded-full bg-red-500" />
      ICD-10 Review
    </span>
  );
}

// Condensed ICD-10 cell for the CRM Queue table row.
// • Empty array (or parse-failed) → "No codes / needs review".
// • Otherwise → comma-separated codes (e.g. "M54.5, M25.531"), truncated.
// • Always renders the review badge to the right when icd10_review is true.
function Icd10Compact({ codes, review }) {
  const list = Array.isArray(codes) ? codes : [];
  const codesText = list.length === 0
    ? "No codes / needs review"
    : list.map((c) => c.code).join(", ");
  return (
    <div className="flex items-center gap-1.5 max-w-[160px]">
      <span
        className={`text-xs font-mono truncate ${list.length === 0 ? "text-slate-400 italic" : "text-slate-700"}`}
        title={list.length === 0
          ? "No ICD-10 codes on this patient"
          : list.map((c) => c.description ? `${c.code} — ${c.description}` : c.code).join("\n")}
      >
        {codesText}
      </span>
      <Icd10ReviewBadge show={review} />
    </div>
  );
}

// ── Stage strip ────────────────────────────────────────────────────────────
// Renders one pill per active calling stage plus a dedicated "Closed"
// group on the right with the 4 terminal stages tucked into a popover.
// `stageFilter` is a string (set via setStageFilter); a numeric id selects
// that one stage. `continuingView` is the separate-team view toggle — it is
// NOT a stage: the Continuing button renders FIRST, visually distinct, with
// a divider before the stage pills. Stage pills render deselected while the
// Continuing view is active (the parent clears stageFilter on entry).
function StageStrip({ stageCounts, stageFilter, setStageFilter, continuingView, setContinuingView, continuingCount, continuingClosedCount = 0, showClosedContinuing = false, setShowClosedContinuing = () => {} }) {
  const [closedOpen, setClosedOpen] = useState(false);
  const closedRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (closedRef.current && !closedRef.current.contains(e.target)) setClosedOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // CRM statuses v2: group tabs with STATUS_NAMES_V2 on; today's strip otherwise.
  const v2 = useStageModel();
  if (v2) {
    return (
      <StageTabsV2 model={v2} stageCounts={stageCounts} stageFilter={stageFilter} setStageFilter={setStageFilter}
        continuingView={continuingView} setContinuingView={setContinuingView} continuingCount={continuingCount}
        continuingClosedCount={continuingClosedCount} showClosedContinuing={showClosedContinuing}
        setShowClosedContinuing={setShowClosedContinuing} />
    );
  }

  const closedCount = CLOSED_STAGE_IDS.reduce((acc, id) => acc + (stageCounts[id] || 0), 0);
  const activeCount = ACTIVE_PILL_STAGE_IDS.reduce((acc, id) => acc + (stageCounts[id] || 0), 0);
  const closedFilterActive = !continuingView && CLOSED_STAGE_IDS.includes(Number(stageFilter));

  return (
    <div ref={closedRef} className="relative">
      <div
        data-testid="stage-strip"
        className="px-8 py-4 flex items-center gap-3 overflow-x-auto border-b border-slate-100 bg-white"
      >
      {/* Continuing view toggle — FIRST element, before all stage pills.
          A separate team works these patients; this is a view switch, not a
          stage filter, hence the distinct (teal, uppercase) styling. */}
      <button
        data-testid="continuing-view-btn"
        onClick={() => setContinuingView(!continuingView)}
        className={`flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-bold uppercase tracking-wide transition-colors ${
          continuingView
            ? "border-teal-500 bg-teal-600 text-white shadow-sm"
            : "border-teal-300 bg-teal-50 text-teal-700 hover:bg-teal-100"
        }`}
        title="Continuing patients — separate team's view"
      >
        <RefreshCw size={12} />
        Continuing
        <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold normal-case ${continuingView ? "bg-white/20 text-white" : "bg-teal-100 text-teal-800"}`}>
          {continuingCount}
        </span>
      </button>

      {/* Item 9 (2026-09-20) — closed Continuing cards are hidden by
          default, which is what finally lets a card LEAVE this view when
          the clinic marks it Completed Initial Visit. The toggle brings
          them back, and its own count says how many that is. Only rendered
          inside the Continuing view. */}
      {continuingView && (
        <button
          type="button"
          data-testid="continuing-show-closed-toggle"
          aria-pressed={showClosedContinuing}
          onClick={() => setShowClosedContinuing(!showClosedContinuing)}
          className={`flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-2 rounded-lg border text-xs font-semibold transition-colors ${
            showClosedContinuing
              ? "border-slate-500 bg-slate-700 text-white"
              : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
          }`}
          title="Closed Continuing cards (Closed: Completed Initial Visit and the other terminal stages) are hidden by default"
        >
          {showClosedContinuing ? "Showing closed" : "Show closed"}
          <span
            data-testid="continuing-closed-count"
            className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${showClosedContinuing ? "bg-white/20 text-white" : "bg-slate-100 text-slate-700"}`}
          >
            {continuingClosedCount}
          </span>
        </button>
      )}

      {/* Divider between the view toggle and the stage pills */}
      <div data-testid="continuing-divider" className="flex-shrink-0 h-6 w-px bg-slate-300 mx-1" />

      {/* Active funnel pills */}
      {ACTIVE_PILL_STAGE_IDS.map((sid) => {
        const s = ALL_STAGES.find((x) => x.id === sid);
        const st = STAGE_STYLES[sid];
        const cnt = stageCounts[sid] || 0;
        const isOn = !continuingView && Number(stageFilter) === sid;
        return (
          <button
            key={sid}
            data-testid={`stage-pill-${sid}`}
            onClick={() => setStageFilter(isOn ? "" : sid)}
            className={`flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
              isOn ? "border-blue-400 bg-blue-50 text-blue-800" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
            }`}
          >
            <span className={`w-2 h-2 rounded-full ${st.dot}`} />
            {s?.name}
            <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${st.bg} ${st.text}`}>{cnt}</span>
          </button>
        );
      })}

      {/* Visual divider */}
      <div className="flex-shrink-0 h-6 w-px bg-slate-200 mx-1" />

      {/* Active / Closed summary */}
      <span
        data-testid="active-count-chip"
        className="flex-shrink-0 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-slate-500 font-semibold"
      >
        Active
        <span className="px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-800 text-xs font-bold normal-case tracking-normal">
          {activeCount}
        </span>
      </span>

      {/* Closed-stages grouped pill */}
      <div className="flex-shrink-0">
        <button
          data-testid="closed-group-btn"
          onClick={() => setClosedOpen((v) => !v)}
          className={`flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
            closedFilterActive
              ? "border-slate-500 bg-slate-100 text-slate-800"
              : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
          }`}
        >
          <span className="w-2 h-2 rounded-full bg-slate-400" />
          Closed
          <span
            data-testid="closed-count-chip"
            className="px-1.5 py-0.5 rounded-full text-xs font-bold bg-slate-100 text-slate-700"
          >
            {closedCount}
          </span>
          <ChevronDown size={11} className={`transition-transform ${closedOpen ? "rotate-180" : ""}`} />
        </button>
      </div>

      {stageFilter !== "" && (
        <button
          data-testid="clear-stage-filter"
          onClick={() => setStageFilter("")}
          className="flex-shrink-0 flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 px-2"
        >
          <X size={12} /> Clear
        </button>
      )}
      </div>
      {closedOpen && (
        <div
          data-testid="closed-group-menu"
          className="absolute right-0 top-full mt-1 w-64 bg-white border border-slate-200 rounded-xl shadow-xl z-50 py-1"
        >
          {CLOSED_STAGE_IDS.map((sid) => {
            const s = ALL_STAGES.find((x) => x.id === sid);
            const st = STAGE_STYLES[sid];
            const cnt = stageCounts[sid] || 0;
            const isOn = !continuingView && Number(stageFilter) === sid;
            return (
              <button
                key={sid}
                data-testid={`stage-pill-${sid}`}
                onClick={() => {
                  setStageFilter(isOn ? "" : sid);
                  setClosedOpen(false);
                }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs hover:bg-slate-50 transition-colors ${
                  isOn ? "bg-slate-50 font-semibold" : ""
                }`}
              >
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${st.dot}`} />
                <span className="text-slate-700 flex-1 text-left">{s?.name}</span>
                <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${st.bg} ${st.text}`}>{cnt}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}



// ── Schedule Modal ────────────────────────────────────────────────────────────
// Reserved appointment types (2026-06): selecting either deliberately fetches
// BOTH reserved categories merged (team-requested 06/09, re-confirmed 08/04).
// The per-slot slotCategory badge + chips below make that merge visible.
const RESERVED_APPT_TYPES = ["RVNP", "RVRA"];
const SLOT_CATEGORY_LABELS = { new_patient: "New Patient", readmit: "Readmit" };
const SLOT_CATEGORY_BADGE_STYLES = {
  new_patient: "bg-sky-100 text-sky-800 border border-sky-300",
  readmit: "bg-amber-100 text-amber-800 border border-amber-300",
};

export function ScheduleModal({ patient, location, dateStart, dateEnd, rescheduleApptId, rescheduleFrom, onClose, onBooked }) {
  const [selectedDate, setSelectedDate] = useState(null);
  const [allSlots, setAllSlots]         = useState([]);  // All slots from Raintree
  const [slots, setSlots]               = useState([]);  // Filtered slots for selected date + provider
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [booking, setBooking]           = useState(false);
  const [bookError, setBookError]       = useState("");
  // Double-book confirm modal — shown only when the active-appointments
  // pre-check returns 1+ rows AND we're not rescheduling. Listed
  // appointments come from /api/crm/patients/{id}/active-appointments which
  // already filters to future, non-cancelled rows.
  const [doubleBookOpen, setDoubleBookOpen] = useState(false);
  const [existingAppts, setExistingAppts]   = useState([]);
  const [slotsSource, setSlotsSource]   = useState(null);  // "raintree" | "error"
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [appointmentTypesGrouped, setAppointmentTypesGrouped] = useState([]);  // Grouped by category
  const [selectedApptType, setSelectedApptType] = useState("PTTX");
  const [selectedProvider, setSelectedProvider] = useState(null);  // Provider code filter
  // clinic-telehealth-book-at: the server says per location whether the
  // calendar may open a Saturday (WS_SATURDAY_BOOKING_LOCATIONS). False until
  // a slot response says otherwise, so the default is today's weekend block.
  const [saturdayBooking, setSaturdayBooking] = useState(false);
  // At a clinic login's extra site (Telehealth) the server allows THTX only,
  // so slots wait until the type list for that site has loaded and set the
  // type. Never true anywhere else, so no other fetch changes.
  const [typesLoadedFor, setTypesLoadedFor] = useState(null);
  const [calMonth, setCalMonth]         = useState(() => {
    const d = dateStart ? new Date(dateStart + "T00:00:00") : new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });

  const isRescheduling = !!rescheduleApptId;
  // Reserved mode: both reserved categories arrive merged — badge + chips.
  const isReservedMode = RESERVED_APPT_TYPES.includes(selectedApptType);
  // Client-side category chip filter (reserved mode only) — zero extra fetches.
  const [categoryFilter, setCategoryFilter] = useState("all");
  useEffect(() => { setCategoryFilter("all"); }, [selectedApptType]);
  const dayCategoryCounts = React.useMemo(() => {
    const counts = { all: slots.length, new_patient: 0, readmit: 0 };
    slots.forEach((s) => {
      const c = s.slotCategory || "general";
      if (counts[c] !== undefined) counts[c] += 1;
    });
    return counts;
  }, [slots]);
  const visibleSlots =
    isReservedMode && categoryFilter !== "all"
      ? slots.filter((s) => (s.slotCategory || "general") === categoryFilter)
      : slots;
  const selectedApptTypeName =
    appointmentTypes.find((t) => t.code === selectedApptType)?.name || "";
  // Network locations (BQ category='WSPT') aren't scheduled in Raintree —
  // we skip the slot-availability webhook and generate every 30-min window
  // 7am–7pm Mon–Fri client-side. Provider is always `_ANY`; appointment
  // type is now a full grouped dropdown of supported network codes
  // (NETWORK_APPOINTMENT_TYPES) per Feb-2026 dedicated webhook contract.
  const isNetwork = !!location?.is_network;

  const rangeStart = dateStart ? new Date(dateStart + "T00:00:00") : null;
  const rangeEnd   = dateEnd   ? new Date(dateEnd   + "T00:00:00") : null;

  // Helpers
  const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
  const isInRange = (d) => rangeStart && rangeEnd && d >= rangeStart && d <= rangeEnd;
  const isWeekend = (d) => d.getDay() === 0 || d.getDay() === 6;

  // Fetch appointment types on mount. For network bookings the only valid
  // types are the centralized NETWORK_APPOINTMENT_TYPES list (PT/AC/AQ),
  // so we anchor on PTEVA and never let the WS default (PTTX) win —
  // otherwise the first booking payload races to 'PTTX' before the
  // network-mode effect can correct it.
  useEffect(() => {
    api.get("/crm/appointment-types", {
      params: {
        ...(location?.id ? { location_id: location.id } : {}),
        // P4 item 12: the card, so a first visit can default to the clinic's
        // evaluation type. Booking only: never when rescheduling or moving.
        ...(!isRescheduling && patient?.id ? { patient_id: String(patient.id) } : {}),
      },
    })
      .then(r => {
        setAppointmentTypes(r.data.appointment_types || []);
        setAppointmentTypesGrouped(r.data.grouped || []);
        setSelectedApptType(prev =>
          isNetwork ? NETWORK_APPOINTMENT_TYPE_DEFAULT
            // P4 part 2: on a switched-on card, Reschedule starts on the visit's own
            // type (so a same-clinic, same-type change is a move, never a silent type change).
            : (patient?.booking_upgrades && isRescheduling && rescheduleFrom?.appointment_type)
              ? rescheduleFrom.appointment_type
              : (r.data.default_type || "PTTX")
        );
        setTypesLoadedFor(location?.id || null);
      })
      .catch(() => {
        setAppointmentTypes([{ code: "PTTX", name: "PT Daily Treatment", category: "Physical Therapy" }]);
        setAppointmentTypesGrouped([{ category: "Physical Therapy", types: [{ code: "PTTX", name: "PT Daily Treatment" }] }]);
        if (isNetwork) setSelectedApptType(NETWORK_APPOINTMENT_TYPE_DEFAULT);
        setTypesLoadedFor(location?.id || null);
      });
    // Item 6 (2026-09-20): the clinic is part of the request now — a
    // location change re-fetches, so the picker always shows that
    // clinic's therapy's types (and Telehealth only at 00020).
  }, [isNetwork, location?.id]);

  // Belt-and-braces: if the user changed locations after the modal mounted
  // and we transitioned from WS → network mid-stream, snap to PTEVA unless
  // the current value is already a supported network appointment-type code.
  useEffect(() => {
    if (isNetwork) {
      setSelectedApptType(prev => (isSupportedNetworkApptType(prev) ? prev : NETWORK_APPOINTMENT_TYPE_DEFAULT));
    }
  }, [isNetwork]);

  // Slots source: Raintree webhook for WS-owned locations, locally-generated
  // 15-min grid for network locations. The two paths feed `allSlots` in the
  // same shape so the rest of the modal (calendar badges, slot picker,
  // selection) renders identically.
  const waitForExtraSiteTypes = !!location?.booking_extra && typesLoadedFor !== location?.id;
  useEffect(() => {
    if (!location || !dateStart || !dateEnd) return;
    if (waitForExtraSiteTypes) return;
    setLoadingSlots(true);
    setAllSlots([]);
    setSlotsSource(null);
    setSelectedProvider(null);
    setSaturdayBooking(false);

    if (isNetwork) {
      // Generate 7:00–19:00 in 15-min steps for every weekday in the range.
      // Provider is always `_ANY` (network clinics have no Raintree provider
      // mapping). `available=true` for every slot; the patient-specific
      // double-book filter below downgrades any conflicts.
      const generated = [];
      const start = new Date(dateStart + "T00:00:00");
      const end   = new Date(dateEnd   + "T00:00:00");
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const day = d.getDay();
        // 2026-08: network clinics book Saturdays; Sunday stays blocked.
        if (day === 0) continue;
        const dateStr = toISO(d);
        for (let h = 7; h <= 19; h++) {
          // 19:00 itself is the closing pin — emit only :00 there, no :15/:30/:45.
          const minutes = h === 19 ? [0] : [0, 15, 30, 45];
          for (const m of minutes) {
            const hh = String(h).padStart(2, "0");
            const mm = String(m).padStart(2, "0");
            const hour12 = ((h + 11) % 12) + 1;
            const ampm = h < 12 ? "AM" : "PM";
            generated.push({
              date: dateStr,
              time: `${hour12}:${mm} ${ampm}`,
              datetime: `${dateStr}T${hh}:${mm}:00`,
              provider: "_ANY",
              provider_name: "Network Location",
              available: true,
            });
          }
        }
      }

      // Patient-specific double-book guard (5a). Mark a slot unavailable if
      // this same patient already has an appointment at the same location +
      // datetime. Different patients sharing a slot at a network clinic is
      // legitimate (those clinics are physically independent businesses).
      api.get(`/crm/patients/${patient.id}/appointments`)
        .then(r => {
          // P4 item 5: the route returns a plain list (it never had an
          // `appointments` key, so this guard never marked a slot), and a cancel
          // deletes the row, so there is no status to check.
          const existing = (Array.isArray(r.data) ? r.data : []).filter(a =>
            a.location_id === location.id
          );
          const conflicts = new Set(existing.map(a => (a.appointment_datetime || "").slice(0, 19)));
          for (const s of generated) {
            if (conflicts.has(s.datetime)) s.available = false;
          }
        })
        .catch(() => { /* non-fatal — server still blocks duplicates */ })
        .finally(() => {
          setAllSlots(generated);
          setSlotsSource("network_local");
          setLoadingSlots(false);
        });
      return;
    }

    api.get("/crm/raintree/slots", {
      params: {
        location_id: location.id,
        from_date: dateStart,
        to_date: dateEnd,
        aptype: selectedApptType,
      }
    })
      .then(r => {
        // Filter out "_ANY" provider slots - only show specific providers
        const slotsWithProviders = (r.data.slots || []).filter(s =>
          s.provider && s.provider !== "_ANY" && s.provider !== "Any"
        );
        setAllSlots(slotsWithProviders);
        setSlotsSource(r.data.source || "raintree");
        setSaturdayBooking(r.data.saturday_booking === true);
      })
      .catch(() => {
        setAllSlots([]);
        setSlotsSource("error");
      })
      .finally(() => setLoadingSlots(false));
  }, [location, dateStart, dateEnd, selectedApptType, isNetwork, patient?.id, waitForExtraSiteTypes]);

  // Filter slots for selected date AND selected provider
  useEffect(() => {
    if (!selectedDate || allSlots.length === 0) {
      setSlots([]);
      return;
    }
    const dateStr = toISO(selectedDate);
    let filtered = allSlots.filter(s => s.date === dateStr);
    
    // If a provider is selected, filter by that provider
    if (selectedProvider) {
      filtered = filtered.filter(s => s.provider === selectedProvider);
    }
    
    setSlots(filtered);
    setSelectedSlot(null);
  }, [selectedDate, allSlots, selectedProvider]);

  // ── P4 part 1 (switched on per card: patient.booking_upgrades) ──────────
  // Off: none of this runs, no request is made, the picker and the box are
  // exactly today's. Never when rescheduling or at a network clinic.
  const upgradesOn = !!patient?.booking_upgrades && !isRescheduling && !isNetwork;
  // P4 part 2 (item 4): Reschedule on a switched-on card at the visit's OWN
  // clinic with its OWN type is a move (same Raintree id); anything else is
  // today's cancel + book.
  // `booking_move` comes from the card only when the move is configured (switch +
  // move URL); otherwise a same-clinic, same-type Reschedule stays today's PUT.
  const moveOn = !!patient?.booking_upgrades && !!patient?.booking_move && isRescheduling && !isNetwork && !!rescheduleFrom
    && rescheduleFrom.location_id === location?.id && rescheduleFrom.appointment_type === selectedApptType;
  const pickerOn = upgradesOn || moveOn;
  const [booked, setBooked]               = useState(null);   // the selected day's booked times + "Yours"
  const [showBooked, setShowBooked]       = useState(false);
  const [bookedConfirm, setBookedConfirm] = useState(null);   // the live count for a clicked booked time

  // Item 1: as soon as a day is picked, fetch its booked times in the
  // background so "Show booked times" is instant.
  useEffect(() => {
    if (!pickerOn || !selectedDate || !location?.id || !patient?.id) {
      setBooked(null);
      setShowBooked(false);
      return undefined;
    }
    const dateStr = toISO(selectedDate);
    let gone = false;
    setBooked({ date: dateStr, loading: true });
    setShowBooked(false);
    api.get("/crm/raintree/booked-slots", {
      params: { patient_id: String(patient.id), location_id: location.id, date: dateStr, aptype: selectedApptType },
    })
      .then(r => { if (!gone) setBooked({ date: dateStr, loading: false, ...r.data }); })
      .catch(() => {
        if (!gone) setBooked({
          date: dateStr, loading: false, enabled: true, slots: [], yours: [],
          full_times: { included: false, error: "schedule_unavailable" },
          full_times_text: "Couldn't load booked times. The open times above are complete.",
        });
      });
    return () => { gone = true; };
  }, [pickerOn, selectedDate, location?.id, selectedApptType, patient?.id]);

  // The box's Cancel only closes it (P4a pin); the live count goes with it.
  useEffect(() => { if (!doubleBookOpen) setBookedConfirm(null); }, [doubleBookOpen]);

  // Item 3: the visits this booking must show first (the one-confirm rule,
  // decided on the server). A failed check never blocks booking (as today).
  const fetchClashRows = async (slot) => {
    try {
      const r = await api.get(`/crm/patients/${patient.id}/active-appointments`, {
        params: { appointment_type: selectedApptType, appointment_datetime: slot.datetime },
      });
      if (r.data && r.data.rule === "one_confirm") return (r.data.rows || []).filter(a => a.warn);
      return Array.isArray(r.data) ? r.data : [];
    } catch (e) {
      console.warn("Clash pre-check failed; proceeding:", e);
      return [];
    }
  };

  // Item 2: a booked time asks for the live count and opens the ONE box
  // (with any same-patient visits in it too).
  const openBookedTime = async (slot) => {
    if (!pickerOn || !slot?.booked || slot.yours) return;
    setSelectedSlot(slot);
    setBookError("");
    setBooking(true);
    try {
      const [occ, rows] = await Promise.all([
        api.post("/crm/raintree/occupancy", {
          patient_id: String(patient.id), location_id: location.id, appointment_datetime: slot.datetime,
          raintree_provider: slot.provider, appointment_type: selectedApptType,
          ...(moveOn ? { exclude_appointment_id: String(rescheduleApptId) } : {}),
        }).then(r => r.data).catch(e => ({
          success: false, offer_book_anyway: false,
          message: formatApiError(e, "Couldn't get the live count from Raintree. Try again."),
        })),
        moveOn ? Promise.resolve([]) : fetchClashRows(slot),
      ]);
      setBookedConfirm({ slot, ...occ, providerName: occ.provider_name || slot.provider_name || slot.provider });
      setExistingAppts(rows);
      setDoubleBookOpen(true);
    } finally {
      setBooking(false);
    }
  };

  // Get unique providers with their total slot counts
  const providerSummary = React.useMemo(() => {
    const summary = {};
    allSlots.forEach(s => {
      const code = s.provider;
      const name = s.provider_name || code;
      if (!summary[code]) {
        summary[code] = { code, name, count: 0 };
      }
      if (s.available !== false) {
        summary[code].count += 1;
      }
    });
    // Sort by slot count descending
    return Object.values(summary).sort((a, b) => b.count - a.count);
  }, [allSlots]);

  // Count available slots per day (for calendar badges) - respects provider filter
  const slotCountByDate = React.useMemo(() => {
    const counts = {};
    const slotsToCount = selectedProvider 
      ? allSlots.filter(s => s.provider === selectedProvider)
      : allSlots;
    slotsToCount.forEach(s => {
      if (s.available !== false) {
        counts[s.date] = (counts[s.date] || 0) + 1;
      }
    });
    return counts;
  }, [allSlots, selectedProvider]);

  // Calendar grid
  const firstOfMonth = new Date(calMonth.year, calMonth.month, 1);
  const daysInMonth  = new Date(calMonth.year, calMonth.month + 1, 0).getDate();
  const startPad     = firstOfMonth.getDay(); // 0=Sun
  const cells        = Array.from({ length: startPad + daysInMonth }, (_, i) =>
    i < startPad ? null : new Date(calMonth.year, calMonth.month, i - startPad + 1)
  );

  const handleBook = async () => {
    if (!selectedSlot || !patient || !location) return;
    // Reschedule path is a slot swap, not a new appointment — the existing
    // row will be replaced anyway, so the double-book warning would be
    // wrong (it'd flag the same appointment we're about to overwrite).
    if (moveOn && selectedSlot.booked) {
      await openBookedTime(selectedSlot);
      return;
    }
    if (isRescheduling) {
      await executeBook();
      return;
    }
    if (upgradesOn && selectedSlot.booked) {
      await openBookedTime(selectedSlot);
      return;
    }
    setBooking(true);
    try {
      if (upgradesOn) {
        const rows = await fetchClashRows(selectedSlot);
        if (rows.length === 0) {
          await executeBook();
          return;
        }
        setExistingAppts(rows);
        setDoubleBookOpen(true);
        return;
      }
      const r = await api.get(`/crm/patients/${patient.id}/active-appointments`);
      const existing = Array.isArray(r.data) ? r.data : [];
      if (existing.length === 0) {
        await executeBook();
        return;
      }
      setExistingAppts(existing);
      setDoubleBookOpen(true);
    } catch (e) {
      // Pre-check failure should NOT block booking — fall through to the
      // normal POST. Worst case the server's own dup-guard catches it.
      console.warn("Active-appointment pre-check failed; proceeding:", e);
      await executeBook();
    } finally {
      setBooking(false);
    }
  };

  // The actual booking call — extracted so both the no-conflict path and
  // the "Book Anyway" confirm modal can call it without duplication.
  const executeBook = async () => {
    if (!selectedSlot || !patient || !location) return;
    setBooking(true);
    setBookError("");
    try {
      const payload = {
        patient_id: String(patient.id),
        location_id: location.id,
        location_name: location.name,
        appointment_datetime: selectedSlot.datetime,
        raintree_provider: selectedSlot.provider,
        appointment_type: selectedApptType,
        // Tell the backend this is a network-clinic booking so it falls
        // back to the BQ-driven Raintree-code lookup (network clinics
        // aren't in raintree_locations.json).
        is_network: !!location.is_network,
        // P4 item 2: only from the booked-time box, only when it was offered.
        ...(bookedConfirm?.offer_book_anyway ? { book_anyway: true } : {}),
      };

      let res;
      if (isRescheduling && moveOn) {
        // P4 item 4: the same Raintree appointment moves (n8n raintree-move).
        res = await api.post(`/crm/appointments/${rescheduleApptId}/move`, {
          appointment_datetime: selectedSlot.datetime,
          raintree_provider: selectedSlot.provider,
          allow_double_book: !!bookedConfirm?.offer_book_anyway,
          patient_id: String(patient.id),
        });
        if (res.data?.success === false) {
          setDoubleBookOpen(false);
          setBookError(res.data.error || "Raintree did not move the visit.");
          return;
        }
      } else if (isRescheduling) {
        // Update existing appointment
        res = await api.put(`/crm/appointments/${rescheduleApptId}`, payload);
      } else {
        // Create new appointment — fail-hard contract: the backend returns
        // success:false (HTTP 200) when Raintree did NOT get the booking,
        // and nothing was persisted. Keep the modal open with selections
        // intact so the user can retry or adjust.
        res = await api.post("/crm/book", payload);
        if (res.data?.success === false) {
          setDoubleBookOpen(false);
          setBookError(res.data.error || "Booking failed — Raintree did not accept the appointment.");
          return;
        }
      }
      setDoubleBookOpen(false);
      setExistingAppts([]);
      onBooked(res.data);
    } catch (e) {
      setBookError(formatApiError(e, "Booking failed — slot may already be taken"));
      setSelectedSlot(null);
    } finally {
      setBooking(false);
    }
  };

  // Group appointment types by category
  const groupedApptTypes = React.useMemo(() => {
    const groups = {};
    appointmentTypes.forEach(t => {
      const cat = t.category || "Other";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(t);
    });
    return groups;
  }, [appointmentTypes]);

  // P4 part 1: with the switch on, mark the patient's own times "Yours" (D1)
  // and, on "Show booked times", mix the day's booked rows in by time (the open
  // row first at the same time). Off: exactly visibleSlots.
  const pickerSlots = (() => {
    if (!pickerOn || !booked || booked.loading) return visibleSlots;
    const yours = new Set(booked.yours || []);
    const mark = (s) => (yours.has((s.datetime || "").slice(0, 16)) ? { ...s, yours: true } : s);
    let list = visibleSlots.map(mark);
    if (showBooked && (booked.slots || []).length) {
      const day = selectedDate ? toISO(selectedDate) : null;
      const extra = booked.slots
        .filter(b => b.date === day && (!selectedProvider || b.provider === selectedProvider))
        .map(b => mark({ ...b }));
      list = [...list, ...extra].sort((a, b) =>
        (a.datetime || "").localeCompare(b.datetime || "") || ((a.booked ? 1 : 0) - (b.booked ? 1 : 0)));
    }
    return list;
  })();

  const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

  const prevMonth = () => setCalMonth(p => {
    const m = p.month === 0 ? 11 : p.month - 1;
    return { year: p.month === 0 ? p.year - 1 : p.year, month: m };
  });
  const nextMonth = () => setCalMonth(p => {
    const m = p.month === 11 ? 0 : p.month + 1;
    return { year: p.month === 11 ? p.year + 1 : p.year, month: m };
  });

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: "rgba(15,23,42,0.65)", backdropFilter: "blur(6px)" }}>
      {/* Double-book confirm modal. Stacked above the schedule modal at
          z-[70] so it captures clicks while keeping the underlying form
          state intact for the Cancel path. Amber palette throughout to
          mark this as a caution, not a hard error. */}
      {doubleBookOpen && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          style={{ background: "rgba(15,23,42,0.55)", backdropFilter: "blur(8px)" }}
          data-testid="double-book-modal"
        >
          {/* Capped like the Schedule modal (flex column, overflow-hidden,
            maxHeight 90vh). The header and the buttons stay pinned and only
            the visit list scrolls: past ~11 upcoming visits the box used to
            run off the screen and hide Book Anyway (clinic report
            2026-10-07). Layout only: what opens the box and what each button
            does are unchanged. */}
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-amber-200 flex flex-col"
            style={{ maxHeight: "90vh" }}
            data-testid="double-book-box"
          >
            <div className="flex-shrink-0" data-testid="double-book-header">
              <div className="px-5 py-4 border-b border-amber-200 bg-amber-50 flex items-center gap-2">
                <AlertTriangle size={18} className="text-amber-600" />
                <h3 className="font-bold text-slate-900">
                  {bookedConfirm && existingAppts.length === 0
                    ? "Time Already Booked"
                    : <>Existing Appointment{existingAppts.length > 1 ? "s" : ""} Found</>}
                </h3>
              </div>
              {/* P4 item 2: the live count for a booked time. One box: a
                  same-patient clash, if any, is listed below in the same box. */}
              {bookedConfirm && (
                <p className="px-5 pt-4 pb-2 text-sm text-slate-700" data-testid="booked-time-message">
                  {bookedConfirm.success && bookedConfirm.booked_count != null && (
                    <span className="block">
                      {bookedConfirm.providerName} already has {bookedConfirm.booked_count}{" "}
                      {bookedConfirm.booked_count === 1 ? "patient" : "patients"} at {bookedConfirm.slot?.time}.
                      {bookedConfirm.offer_book_anyway ? " Book anyway?" : ""}
                    </span>
                  )}
                  {bookedConfirm.message && (
                    <span className="block mt-1 text-amber-800" data-testid="booked-time-error">{bookedConfirm.message}</span>
                  )}
                </p>
              )}
              {existingAppts.length > 0 && (
                <p className="px-5 pt-4 pb-2 text-sm text-slate-700">
                  This patient already has{" "}
                  <span className="font-semibold">
                    {existingAppts.length === 1 ? "an appointment" : `${existingAppts.length} appointments`}
                  </span>:
                </p>
              )}
            </div>
            <ul
              className="flex-1 min-h-0 overflow-y-auto px-5 py-1 space-y-1 text-sm text-slate-700"
              data-testid="double-book-list"
            >
              {existingAppts.map((a, i) => {
                // 2026-06-02: use the shared wall-clock formatter so the
                // time the staff member picked renders as-is (TIMESTAMPTZ
                // round-trip from the backend would otherwise render
                // in the browser's local zone — see lib/wallClock.js for the
                // full diagnosis).
                const dateStr = formatWallClockDate(a.appointment_datetime);
                const timeStr = formatWallClockTime(a.appointment_datetime);
                // One compact line per visit, same fields in the same order:
                // date, time, location, therapist (P4 item 6), type. Only the
                // location truncates.
                return (
                  <li
                    key={a.id ?? i}
                    className="rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-1.5 flex items-center gap-3 min-w-0"
                    data-testid={`double-book-existing-${i}`}
                  >
                    <span className="font-semibold text-slate-900 whitespace-nowrap">{dateStr}</span>
                    <span className="font-mono text-slate-700 whitespace-nowrap">{timeStr}</span>
                    <span
                      className="flex-1 min-w-0 truncate text-xs font-medium text-slate-600"
                      title={a.location_name || a.location_id || "Unknown location"}
                    >
                      {a.location_name || a.location_id || "Unknown location"}
                    </span>
                    {a.provider_name && (
                      <span
                        className="flex-shrink-0 max-w-[9rem] truncate text-xs text-slate-600"
                        title={a.provider_name}
                        data-testid={`double-book-provider-${i}`}
                      >
                        {a.provider_name}
                      </span>
                    )}
                    {a.appointment_type && (
                      <span className="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-md bg-white border border-amber-300 text-amber-800 font-mono text-[10px]">
                        {a.appointment_type}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
            <div className="flex-shrink-0" data-testid="double-book-footer">
              {!bookedConfirm && (
                <p className="px-5 py-3 text-sm text-slate-700 border-t border-amber-100">Are you sure you want to book another appointment?</p>
              )}
              <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex justify-end gap-2">
                <button
                  onClick={() => { setDoubleBookOpen(false); setExistingAppts([]); }}
                  disabled={booking}
                  className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200 rounded-lg disabled:opacity-50"
                  data-testid="double-book-cancel-btn"
                >
                  Cancel
                </button>
                {(!bookedConfirm || bookedConfirm.offer_book_anyway) && (
                  <button
                    onClick={executeBook}
                    disabled={booking}
                    className="px-4 py-2 text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-lg disabled:opacity-50 flex items-center gap-2"
                    data-testid="double-book-confirm-btn"
                  >
                    {booking ? <Loader2 size={14} className="animate-spin" /> : <AlertTriangle size={14} />}
                    {bookedConfirm ? "Book anyway" : "Book Anyway"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl border border-slate-100 overflow-hidden flex flex-col" style={{ maxHeight: "90vh", animation: "slideUp 0.25s ease-out" }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center">
              <CalendarCheck size={18} className="text-blue-700" />
            </div>
            <div>
              <p className="font-bold text-slate-900">{isRescheduling ? "Reschedule Appointment" : "Schedule Appointment"}</p>
              <p className="text-xs text-slate-500">
                {patient?.full_name} · <span className="font-medium text-blue-700">{location?.name}</span>
                {location?.raintree_code && (
                  <span className="ml-1 text-slate-400">(RT: {location.raintree_code})</span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Raintree status badge */}
            {slotsSource === "raintree" && !loadingSlots && (
              <span className="flex items-center gap-1 text-xs text-emerald-600 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-full font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Live Raintree
              </span>
            )}
            {slotsSource === "network_local" && !loadingSlots && (
              <span className="flex items-center gap-1 text-xs text-amber-700 bg-amber-50 border border-amber-300 px-2 py-1 rounded-full font-medium" data-testid="network-mode-badge">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                Network Location
              </span>
            )}
            {slotsSource === "error" && !loadingSlots && (
              <span className="flex items-center gap-1 text-xs text-red-600 bg-red-50 border border-red-200 px-2 py-1 rounded-full font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                Connection Error
              </span>
            )}
            {loadingSlots && (
              <span className="flex items-center gap-1 text-xs text-blue-600 bg-blue-50 border border-blue-200 px-2 py-1 rounded-full font-medium">
                <span className="w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                Loading...
              </span>
            )}
            <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Persistent booking-failure banner — required failure indication.
            Does NOT auto-dismiss; stays until dismissed or a retry succeeds. */}
        {bookError && (
          <div
            className="mx-6 mt-4 flex items-start gap-3 rounded-xl border border-red-300 bg-red-50 px-4 py-3 flex-shrink-0"
            data-testid="booking-error-banner"
          >
            <AlertTriangle size={18} className="text-red-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 text-sm">
              <p className="font-semibold text-red-800">Booking failed — nothing was saved</p>
              <p className="text-red-700 mt-0.5" data-testid="booking-error-message">{bookError}</p>
            </div>
            <button
              onClick={() => setBookError("")}
              className="p-1 rounded-lg text-red-400 hover:text-red-700 hover:bg-red-100 transition-colors flex-shrink-0"
              data-testid="booking-error-dismiss"
              aria-label="Dismiss booking error"
            >
              <X size={16} />
            </button>
          </div>
        )}

        <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
          {/* Left — Calendar */}
          <div className="flex-1 p-5 border-r border-slate-100 overflow-y-auto">
            {/* Appointment Type — full grouped dropdown for both branches.
                WS-internal dropdown uses live Raintree appt types from the
                /crm/appointment-types endpoint; network dropdown uses the
                centralized NETWORK_APPOINTMENT_TYPES_BY_CATEGORY constant
                (PT × 6, AC × 2, AQ × 2 — TH/RV excluded by design). */}
            <div className="mb-4">
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                Appointment Type {isNetwork && <span className="text-amber-700 font-normal normal-case">(Network)</span>}
              </label>
              {isNetwork ? (
                <select
                  data-testid="network-appt-type-select"
                  value={selectedApptType}
                  onChange={(e) => setSelectedApptType(e.target.value)}
                  className="w-full h-10 px-3 rounded-xl border border-amber-300 text-sm font-medium text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all"
                >
                  {NETWORK_APPOINTMENT_TYPES_BY_CATEGORY.map(({ category, types }) => (
                    <optgroup key={category} label={category}>
                      {types.map(t => (
                        <option key={t.code} value={t.code}>
                          {t.code} — {t.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              ) : (
                <select
                  data-testid="appt-type-select"
                  value={selectedApptType}
                  onChange={(e) => setSelectedApptType(e.target.value)}
                  className="w-full h-10 px-3 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                >
                  {Object.entries(groupedApptTypes).map(([category, types]) => (
                    <optgroup key={category} label={category}>
                      {types.map(t => (
                        <option key={t.code} value={t.code}>
                          {t.code} — {t.name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              )}
            </div>

            <div className="flex items-center justify-between mb-4">
              <button onClick={prevMonth} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors"><ChevronLeft size={16} /></button>
              <span className="font-semibold text-slate-800">{MONTHS[calMonth.month]} {calMonth.year}</span>
              <button onClick={nextMonth} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors"><ChevronRight size={16} /></button>
            </div>
            <div className="grid grid-cols-7 gap-0.5 mb-1">
              {DAYS.map(d => <span key={d} className="text-center text-xs font-semibold text-slate-400 py-1">{d}</span>)}
            </div>
            <div className="grid grid-cols-7 gap-0.5">
              {cells.map((d, i) => {
                if (!d) return <div key={`empty-${i}`} />;
                const iso = toISO(d);
                const inRange = isInRange(d);
                // 2026-08: network clinics book Saturdays (Sunday still
                // blocked); the WS/Raintree picker keeps its weekend block.
                const wknd = isNetwork ? d.getDay() === 0 : isWeekend(d);
                const isSelected = selectedDate && toISO(selectedDate) === iso;
                const isToday = toISO(new Date()) === iso;
                const slotCount = slotCountByDate[iso] || 0;
                const hasSlots = slotCount > 0;
                // clinic-telehealth-book-at: a Saturday opens only where the
                // server said so AND Raintree returned slots that day. With
                // WS_SATURDAY_BOOKING_LOCATIONS empty this equals `wknd`.
                const wkndBlocked = wknd && !(!isNetwork && saturdayOpen(d, saturdayBooking, slotCount));
                let cls = "relative h-10 w-full flex flex-col items-center justify-center rounded-lg text-sm font-medium transition-all ";
                if (isSelected) cls += "bg-blue-600 text-white shadow-sm ";
                else if (inRange && !wkndBlocked && hasSlots) cls += "bg-emerald-50 text-emerald-800 cursor-pointer hover:bg-emerald-100 border border-emerald-200 ";
                else if (inRange && !wkndBlocked) cls += "bg-slate-50 text-slate-400 cursor-pointer hover:bg-slate-100 ";
                else if (inRange && wkndBlocked) cls += "bg-slate-50 text-slate-300 cursor-not-allowed ";
                else cls += "text-slate-300 cursor-not-allowed ";
                if (isToday && !isSelected) cls += "ring-1 ring-blue-300 ";
                return (
                  <button
                    key={iso}
                    disabled={!inRange || wkndBlocked}
                    onClick={() => setSelectedDate(d)}
                    className={cls}
                    title={inRange && !wkndBlocked ? (hasSlots ? `${slotCount} slots available` : "No slots available") : wkndBlocked ? "Weekends unavailable" : "Outside selected range"}
                  >
                    <span>{d.getDate()}</span>
                    {inRange && !wkndBlocked && hasSlots && !isSelected && (
                      <span className="text-[9px] font-bold text-emerald-600">{slotCount}</span>
                    )}
                  </button>
                );
              })}
            </div>
            {rangeStart && rangeEnd && (
              <p className="mt-4 text-xs text-slate-400 text-center">
                Showing {rangeStart.toLocaleDateString()} → {rangeEnd.toLocaleDateString()}
              </p>
            )}
          </div>

          {/* Right — Time slots */}
          <div className="w-full md:w-56 p-5 flex flex-col overflow-y-auto flex-shrink-0">
            {!selectedDate ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center gap-2">
                <CalendarDays size={32} className="text-slate-200" />
                <p className="text-sm text-slate-400">Select a date to see available times</p>
              </div>
            ) : loadingSlots ? (
              <div className="flex-1 flex items-center justify-center">
                <span className="inline-block w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <>
                <p className="text-xs font-semibold text-slate-500 mb-3 uppercase tracking-wider">
                  {selectedDate.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                </p>
                {isReservedMode && slots.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-3" data-testid="slot-category-chips">
                    {[
                      { key: "all", label: "All" },
                      { key: "new_patient", label: "New Patient" },
                      { key: "readmit", label: "Readmit" },
                    ].map(({ key, label }) => (
                      <button
                        key={key}
                        type="button"
                        data-testid={`slot-chip-${key}`}
                        onClick={() => setCategoryFilter(key)}
                        className={`px-2 py-1 rounded-full text-[11px] font-semibold border transition-colors ${
                          categoryFilter === key
                            ? "bg-blue-600 text-white border-blue-600"
                            : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"
                        }`}
                      >
                        {label} ({dayCategoryCounts[key] ?? 0})
                      </button>
                    ))}
                  </div>
                )}
                {pickerOn && booked?.enabled && booked.full_times?.error !== "not_configured" && (
                  booked.loading || booked.full_times?.included ? (
                    <button
                      type="button"
                      onClick={() => setShowBooked(v => !v)}
                      className="mb-2 px-2 py-1 rounded-lg text-xs font-semibold border border-slate-200 text-slate-600 hover:border-blue-300 flex items-center gap-1.5"
                      data-testid="toggle-booked-times"
                    >
                      {showBooked && booked.loading && <Loader2 size={12} className="animate-spin" />}
                      {showBooked ? "Hide booked times" : "Show booked times"}
                    </button>
                  ) : (
                    <p className="mb-2 text-xs text-slate-500" data-testid="booked-times-note">{booked.full_times_text}</p>
                  )
                )}
                <div className="flex-1 space-y-1.5 overflow-y-auto">
                  {pickerSlots.length === 0 && <p className="text-xs text-slate-400 text-center py-4">No slots available</p>}
                  {pickerSlots.map(slot => {
                    // Create unique slot ID combining datetime and provider
                    const slotId = `${slot.datetime}-${slot.provider}`;
                    const selectedId = selectedSlot ? `${selectedSlot.datetime}-${selectedSlot.provider}` : null;
                    const isSel = slotId === selectedId;
                    const providerDisplay = slot.provider_name || slot.provider || "Any";
                    if (pickerOn && (slot.booked || slot.yours)) {
                      // P4: a booked time says "Booked" (never a number) and opens the
                      // live count; the patient's own time says "Yours" and does nothing.
                      return (
                        <button
                          key={`${slotId}-${slot.booked ? "booked" : "open"}`}
                          type="button"
                          disabled={!!slot.yours || booking}
                          onClick={() => openBookedTime(slot)}
                          data-testid={`slot-${slot.yours ? "yours" : "booked"}-${slot.time.replace(/[: ]/g, "-")}-${slot.provider}`}
                          className={`w-full px-3 py-2 rounded-xl text-sm font-medium text-left transition-all ${
                            slot.yours
                              ? "bg-blue-50 text-blue-700 border border-blue-200 cursor-not-allowed"
                              : "bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100"
                          }`}
                        >
                          <div className="flex flex-col">
                            <span className="font-semibold">{slot.time}</span>
                            <span className="text-xs">{providerDisplay}</span>
                          </div>
                          <span className="text-xs ml-1">{slot.yours ? "Yours" : "Booked"}</span>
                        </button>
                      );
                    }
                    return (
                      <button
                        key={slotId}
                        disabled={!slot.available}
                        onClick={() => setSelectedSlot(isSel ? null : slot)}
                        data-testid={`slot-${slot.time.replace(/[: ]/g, "-")}-${slot.provider}`}
                        className={`w-full px-3 py-2 rounded-xl text-sm font-medium text-left transition-all ${
                          !slot.available
                            ? "bg-slate-50 text-slate-300 cursor-not-allowed line-through"
                            : isSel
                            ? "bg-blue-600 text-white shadow-sm"
                            : "bg-emerald-50 text-emerald-800 hover:bg-emerald-100 border border-emerald-200"
                        }`}
                      >
                        <div className="flex flex-col">
                          <span className="font-semibold flex items-center gap-1.5 flex-wrap">
                            {slot.time}
                            {isReservedMode && SLOT_CATEGORY_LABELS[slot.slotCategory] && (
                              <span
                                data-testid={`slot-category-badge-${slot.slotCategory}`}
                                className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                                  isSel ? "bg-white/20 text-white border border-white/30" : SLOT_CATEGORY_BADGE_STYLES[slot.slotCategory]
                                }`}
                              >
                                {SLOT_CATEGORY_LABELS[slot.slotCategory]}
                              </span>
                            )}
                          </span>
                          <span className={`text-xs ${isSel ? "text-blue-100" : "text-emerald-600"}`}>
                            {providerDisplay}
                          </span>
                        </div>
                        {!slot.available && <span className="text-xs ml-1">(booked)</span>}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Provider Selection Section — hidden for network locations
            (every network slot is `_ANY` provider, so the sidebar would
            only show one row with no useful filtering). */}
        {!isNetwork && !loadingSlots && providerSummary.length > 0 && (
          <div className="px-6 py-4 border-t border-slate-100 bg-gradient-to-b from-slate-50 to-white">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <User size={14} className="text-slate-500" />
                <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Select Provider</h4>
              </div>
              {selectedProvider && (
                <button
                  onClick={() => setSelectedProvider(null)}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  Show All Providers
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {providerSummary.map(p => {
                const isSelected = selectedProvider === p.code;
                return (
                  <button
                    key={p.code}
                    onClick={() => setSelectedProvider(isSelected ? null : p.code)}
                    data-testid={`provider-${p.code}`}
                    className={`px-3 py-2 rounded-xl text-sm font-medium transition-all border ${
                      isSelected
                        ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                        : "bg-white text-slate-700 border-slate-200 hover:border-blue-300 hover:bg-blue-50"
                    }`}
                  >
                    <span className="font-semibold">{p.name}</span>
                    <span className={`ml-2 px-1.5 py-0.5 rounded text-xs ${
                      isSelected ? "bg-blue-500 text-blue-100" : "bg-emerald-100 text-emerald-700"
                    }`}>
                      {p.count} {p.count === 1 ? "slot" : "slots"}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50 flex-shrink-0">
          <div className="text-sm text-slate-500">
            {selectedSlot ? (
              <span className="font-medium text-slate-800">
                <span className="text-blue-700">{selectedDate?.toLocaleDateString("en-US", { weekday:"long", month:"short", day:"numeric" })}</span>
                {" at "}<span className="text-blue-700">{selectedSlot.time}</span>
                {selectedSlot.provider_name && (
                  <span className="text-slate-500"> with <span className="text-blue-700">{selectedSlot.provider_name}</span></span>
                )}
                {isReservedMode && SLOT_CATEGORY_LABELS[selectedSlot.slotCategory] && (
                  <span
                    data-testid={`selected-slot-category-badge-${selectedSlot.slotCategory}`}
                    className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold align-middle ${SLOT_CATEGORY_BADGE_STYLES[selectedSlot.slotCategory]}`}
                  >
                    {SLOT_CATEGORY_LABELS[selectedSlot.slotCategory]}
                  </span>
                )}
                {!isReservedMode && (
                  // Booking-type honesty (2026-06): regular slots arrive as
                  // an untyped open grid — the type is a BOOKING-time
                  // decision, not an availability filter.
                  <span className="block text-xs text-slate-500 mt-0.5" data-testid="books-as-line">
                    Books as: <span className="font-semibold text-slate-700">{selectedApptType}</span>
                    {selectedApptTypeName ? ` — ${selectedApptTypeName}` : ""}
                  </span>
                )}
              </span>
            ) : selectedProvider ? (
              <span className="text-slate-600">
                Showing slots for <span className="font-semibold text-blue-700">{providerSummary.find(p => p.code === selectedProvider)?.name}</span>
              </span>
            ) : (
              "Select a provider, then choose a date and time"
            )}
          </div>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors">Cancel</button>
            <button
              data-testid="confirm-appointment-btn"
              onClick={handleBook}
              disabled={!selectedSlot || booking}
              className="px-5 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-40 transition-colors flex items-center gap-2 shadow-sm"
            >
              {booking ? <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <CalendarCheck size={15} />}
              {isRescheduling ? "Confirm Reschedule" : "Confirm Appointment"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Network Scheduling status badge ───────────────────────────────────────────
// Shows the current state of a patient's in-flight scheduling request.
// `locations` is the same array fed to QueueBanner — we use it to render
// the friendly clinic name instead of the raw slug.
function SchedulingStatusBadge({ request, locations }) {
  if (!request) return null;
  const loc = (locations || []).find(l => l.id === request.location_code);
  // PR #9 (2026-10-05): WestStar slugs aren't in the network list, so fall
  // back to the roster name + city the server now sends, then the raw slug.
  const clinicName = loc?.name || pickClinicLabel(request.clinic) || request.location_code;
  const isPatientPick = isPatientPickRequest(request);

  let tone, label, detail;
  if (isPatientPick) {
    // The patient's OWN pick from the T-01 link: who, where, and every window.
    tone = "bg-sky-50 border-sky-200 text-sky-900";
    label = `Patient picked ${clinicName}`;
    detail = formatPickWindows(request.proposed_times);
  } else if (request.status === "pending") {
    tone = "bg-yellow-50 border-yellow-200 text-yellow-800";
    label = `Pending at ${clinicName}`;
    detail = "Awaiting clinic response.";
  } else if (request.status === "suggested_alternative") {
    tone = "bg-orange-50 border-orange-200 text-orange-800";
    label = `Alternative Suggested · ${clinicName}`;
    detail = "Clinic proposed different times — review and lock in or counter.";
  } else if (request.status === "locked_in" || request.status === "confirmed") {
    tone = "bg-emerald-50 border-emerald-200 text-emerald-800";
    const ct = request.confirmed_time;
    const when = ct
      ? `${new Date(`${ct.date}T${ct.start_time}`).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}`
      : "—";
    label = `Confirmed · ${when}`;
    detail = `${clinicName}`;
  } else {
    tone = "bg-slate-50 border-slate-200 text-slate-700";
    label = `Scheduling: ${request.status}`;
    detail = clinicName;
  }

  return (
    <div
      data-testid="scheduling-status-badge"
      className={`col-span-2 flex items-center justify-between gap-3 p-3 rounded-xl border ${tone}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <CalendarDays size={18} className="flex-shrink-0 opacity-80" />
        <div className="min-w-0">
          <p className="text-sm font-semibold truncate">{label}</p>
          <p className={`text-xs opacity-80 ${isPatientPick ? "whitespace-normal" : "truncate"}`}>{detail}</p>
        </div>
      </div>
      <span
        className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-white/70 flex-shrink-0"
        data-testid={`scheduling-status-${isPatientPick ? "patient_pick" : request.status}`}
      >
        {isPatientPick ? "patient pick" : request.status.replace(/_/g, " ")}
      </span>
    </div>
  );
}

// ── Lock banner (modal, read-only mode) ──────────────────────────────────────
// Read-only used to be SILENT: `lockedBy` was passed to QueueBanner and never
// rendered, so a locked patient just showed no edit controls and no reason.
// `locked_at` is a TRUE INSTANT (server `datetime.now(timezone.utc)`), so
// converting it to the viewer's clock is correct here — unlike appointment
// stamps, which are wall clock and go through lib/wallClock.js.
export function LockBanner({ lockedBy, lockInfo, onTakeover }) {
  const [takingOver, setTakingOver] = useState(false);
  const at = lockInfo?.locked_at ? new Date(lockInfo.locked_at) : null;
  const when = at && !isNaN(at)
    ? at.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    : null;
  const isSelf = !!lockInfo?.is_self;
  const who = lockedBy || lockInfo?.locked_by_username || "another user";
  const message = isSelf
    ? `This patient is open in your own earlier session${when ? ` from ${when}` : ""}. Close that window or take over here to edit.`
    : `Locked by ${who}${when ? ` since ${when}` : ""} — you can view, but not edit.`;

  const handleClick = async () => {
    if (!onTakeover) return;
    if (!window.confirm(`Take over editing from ${who}? They will lose editing on this patient.`)) return;
    setTakingOver(true);
    try {
      await onTakeover();
    } finally {
      setTakingOver(false);
    }
  };

  return (
    <div
      className="mx-6 mt-4 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3"
      data-testid="lock-banner"
    >
      <Lock size={16} className="mt-0.5 flex-shrink-0 text-amber-600" />
      <p className="flex-1 text-sm text-amber-900" data-testid="lock-banner-message">
        {message}
      </p>
      {onTakeover && lockInfo?.can_take_over && (
        <button
          onClick={handleClick}
          disabled={takingOver}
          className="flex-shrink-0 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-amber-700 disabled:opacity-50"
          data-testid="lock-takeover-btn"
        >
          {takingOver ? "Taking over…" : "Take over editing"}
        </button>
      )}
    </div>
  );
}

// ── Queue Banner (modal) ──────────────────────────────────────────────────────
function QueueBanner({ patient, onAction, onRelease, onNext, serveReason = null, serveReasonIsSnapshot = false, acting, locations, isDirectView, onNotesSaved, onPatientUpdated, onActionRecorded = null, actionSaved = false, readOnly = false, lockedBy = null, lockInfo = null, onTakeover = null }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const writing = useWritesInFlight(); // 2026-10-24: Next and Close wait for this card's saves
  // readOnly = another rep holds the lock (409 on acquire) — viewing stays
  // allowed, every edit affordance is disabled.
  // Commit 2 (2026-09): the WS Location role edits its OWN patients exactly
  // as internal staff do — same pencils, same endpoint, same audit rows and
  // locally_modified protection. The patient boundary is server-side
  // (Commit 1's scope guard), so this flag only decides what renders.
  const isWsLocationUser = user?.role === "location";
  const canEditFields = !readOnly && (user?.role === "admin" || user?.role === "sales" || isWsLocationUser);
  // Item 4 (C2, 2026-09-21) — the location role edits the ticket's NAMED
  // GROUP only (contact details + DOB). Everything else renders without a
  // pencil for that role, and the server 403s it with zero writes; this
  // flag only decides what a human can reach. Sales/admin: unchanged, the
  // expression short-circuits before the list is consulted.
  const canEditField = (fieldName) =>
    canEditFields && (!isWsLocationUser || LOCATION_EDITABLE_FIELDS.includes(fieldName));
  // ...but REASSIGNING a patient to another clinic stays sales/admin
  // (`PUT /assigned-clinic` is still gated to them). For a WS Location user
  // the dropdown is a BOOKING selector, never an assignment control — the
  // affordance is absent rather than inert, so nobody believes they moved a
  // patient they didn't.
  const canReassignClinic = !readOnly && (user?.role === "admin" || user?.role === "sales");
  const { codes: myLocationCodes, extraCodes: myExtraBookingCodes } = useMyLocationCodes();
  
  const today = new Date().toISOString().slice(0, 10);
  const nextWeek = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);

  const [selectedLocation, setSelectedLocation] = useState(null);
  const [dateStart, setDateStart]               = useState(today);
  const [dateEnd, setDateEnd]                   = useState(nextWeek);
  const [showSchedule, setShowSchedule]         = useState(false);
  const [rescheduleApptId, setRescheduleApptId] = useState(null);  // ID of appointment being rescheduled
  const [rescheduleFrom, setRescheduleFrom] = useState(null);      // P4: that visit's clinic and type
  // 2026-06-02: `bookedInfo` state was removed along with the blue
  // "Appointment Scheduled" banner. The canonical purple "Scheduled
  // Appointments" section below already shows the just-booked row
  // once `setAppointments(...)` refreshes, and the sonner toast
  // fired from `handleBannerAction` provides the success
  // confirmation. No behavioural change beyond removing redundant UI.
  const [notes, setNotes]                       = useState(patient.notes || "");
  const [notesTimestamp, setNotesTimestamp]     = useState({ at: patient.notes_updated_at, by: patient.notes_updated_by });
  const [notesSaving, setNotesSaving]           = useState(false);
  const [notesSaved, setNotesSaved]             = useState(false);
  const [appointments, setAppointments]         = useState([]);
  // P3: live Raintree states for the visits panel. Off unless the endpoint
  // answers enabled for this card (CARD_LIVE_VISITS_LOCATIONS).
  const [liveVisits, setLiveVisits] = useState(null);
  const [liveChecking, setLiveChecking] = useState(false);
  const [cancelRefused, setCancelRefused] = useState(() => new Set());
  const [cancelNotice, setCancelNotice] = useState(null);   // P3: the one refused/failed-cancel message
  const liveAbortRef = useRef(null);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  const [cancellingApptId, setCancellingApptId] = useState(null);  // ID of appointment being cancelled
  const [patientData, setPatientData]           = useState(patient);  // Local copy for editable fields
  
  // Callback scheduling state
  const [showCallbackModal, setShowCallbackModal] = useState(false);
  const [callbackDate, setCallbackDate] = useState("");
  const [callbackTime, setCallbackTime] = useState("");
  const [callbackReason, setCallbackReason] = useState("");
  const [callbackSaving, setCallbackSaving] = useState(false);

  // Update local patient data when patient prop changes
  useEffect(() => {
    setPatientData(patient);
  }, [patient]);

  // 2026-06-05: Reset all per-patient input form state when switching to
  // a different patient. Without this, useState initializers only run on
  // mount and the previous patient's typed-but-unsaved values bleed into
  // the new patient's screen — which can result in saving the wrong
  // patient's notes/callback into the new patient's database record
  // (the save handlers POST to the URL's current patient_id but the
  // request body carries the stale local-state values). Mirrors the
  // setPatientData(patient) pattern above for inline-editable fields.
  //
  // NOTE: `notesSaving` and `callbackSaving` are intentionally NOT reset
  // here — they reflect in-flight save requests and will naturally
  // settle when those calls complete.
  useEffect(() => {
    setNotes(patient?.notes || "");
    setNotesTimestamp({ at: patient?.notes_updated_at, by: patient?.notes_updated_by });
    setNotesSaved(false);
    setCallbackDate("");
    setCallbackTime("");
    setCallbackReason("");
  }, [patient?.id]);

  // Assigned-clinic pair confirmation dialog state (2026-02). Kept OUT of
  // any auto-select codepath — this state is only ever set by the user's
  // explicit change on the location <select>. See onChange at the dropdown
  // and the guard `_isUserInitiated` comment.
  const [assignedClinicConfirm, setAssignedClinicConfirm] = useState(null);
  const [assignedClinicSaving, setAssignedClinicSaving] = useState(false);

  // Item 4 (C2, 2026-09-21) — DOB is an identity key staff match patients
  // on, so a change is CONFIRMED before it leaves the browser. The dialog
  // resolves the field's own save promise: confirm → the normal save path;
  // cancel → the field stays in edit mode with the typed value intact.
  const [dobConfirm, setDobConfirm] = useState(null);

  // Save editable field
  const handleSaveField = async (fieldName, newValue) => {
    const response = await api.put(`/crm/patients/${patient.id}/edit`, {
      fields: { [fieldName]: newValue }
    });
    if (response.data?.success) {
      // Update local state
      setPatientData(prev => ({ ...prev, [fieldName]: newValue }));
      // Notify parent to refresh table
      if (onPatientUpdated) {
        onPatientUpdated(patient.id, { [fieldName]: newValue });
      }
    }
    return response;
  };

  // DOB's save is gated by the confirmation dialog (item 4, C2).
  const handleSaveDob = (fieldName, newValue) =>
    new Promise((resolve, reject) => {
      setDobConfirm({ fieldName, newValue, resolve, reject });
    });

  // ── Assigned-clinic pair writer (2026-02) ────────────────────────────
  // Single-source-of-truth handoff: the location dropdown persists the
  // patient's assigned_clinic + assigned_clinic_city pair via the paired
  // endpoint, marks both fields locally_modified, and lets the derived
  // location_class + master-filter bucket flip automatically on the next
  // serialization. Every write is USER-INITIATED — this function is only
  // reachable from the <select onChange> handler below, never from any
  // auto-pick or programmatic seeding (a test pins this).
  const persistAssignedClinicPair = async (nextLoc) => {
    setAssignedClinicSaving(true);
    try {
      const res = await api.put(`/crm/patients/${patient.id}/assigned-clinic`, {
        name: nextLoc?.clinic_name ?? null,
        city: nextLoc?.city ?? null,
      });
      if (res.data?.success) {
        const next = {
          assigned_clinic: res.data.assigned_clinic,
          assigned_clinic_city: res.data.assigned_clinic_city,
          location_class: res.data.location_class,
        };
        setPatientData(prev => ({ ...prev, ...next }));
        if (onPatientUpdated) onPatientUpdated(patient.id, next);
        // Item 5 (C2, 2026-09-21): a front-desk transfer can hand the
        // patient to a clinic outside this user's own codes — the fence
        // moved with the pair, so the card is about to disappear from
        // their queue. Say so, then close the panel.
        const newCode = res.data.assigned_location_code || null;
        if (isWsLocationUser && !(myLocationCodes || []).includes(newCode)) {
          setTransferredAway({
            clinic: res.data.assigned_clinic,
            city: res.data.assigned_clinic_city,
          });
        }
      }
      return res.data;
    } catch (err) {
      console.error("assigned-clinic pair save failed:", err);
      alert(formatApiError(err, "Failed to update assigned clinic"));
    } finally {
      setAssignedClinicSaving(false);
    }
  };

  // Decide whether the ticket's confirmation conditions fire for a
  // proposed dropdown change. Both are exposed for tests.
  //
  //   Cond A — classification flip (WestStar ↔ Network).
  //     Compares the new location's `is_network` to the CURRENT patient's
  //     `location_class`. A stringly-derived 'network' vs `is_network===true`
  //     match keeps the classifier itself untouched (pin, don't reimplement).
  //
  //   Cond B — patient has a future appointment at the CURRENT location.
  //     Any appointment row with `appointment_datetime > now` whose
  //     `location_name` matches (case-insensitive, trimmed) the patient's
  //     current `assigned_clinic`. Kept strict per ticket wording — not
  //     "any future appointment", specifically "at the current location".
  const _assignedClinicConfirmReasons = (nextLoc) => {
    const reasons = [];
    const currentClass = (patientData?.location_class || "").toLowerCase();
    const nextClass = nextLoc?.is_network ? "network" : "weststar";
    if (currentClass && currentClass !== "unassigned" && currentClass !== nextClass) {
      reasons.push({
        kind: "classification_flip",
        from: currentClass,
        to: nextClass,
      });
    }
    const nowMs = Date.now();
    const currentName = (patientData?.assigned_clinic || "").trim().toLowerCase();
    if (currentName) {
      const futureAtCurrent = (appointments || []).filter(a => {
        const dtMs = a?.appointment_datetime ? new Date(a.appointment_datetime).getTime() : 0;
        if (!dtMs || dtMs <= nowMs) return false;
        const ln = (a?.location_name || "").trim().toLowerCase();
        return ln === currentName;
      });
      if (futureAtCurrent.length) {
        reasons.push({
          kind: "future_appt_at_current",
          appointments: futureAtCurrent.map(a => ({
            id: a.id,
            when: a.appointment_datetime,
            location_name: a.location_name,
          })),
        });
      }
    }
    return reasons;
  };

  // User-initiated dropdown handler. Gated so ANY auto-pick / programmatic
  // seeding of `selectedLocation` (auto-select useEffect, reschedule-modal
  // seed) does NOT reach here — the write to /assigned-clinic ONLY happens
  // when this function runs.
  const onLocationDropdownChange = (locId) => {
    const nextLoc = (filteredLocations || []).find(l => l.id === locId) || null;
    setSelectedLocation(nextLoc);
    setLocationMatchType("manual");
    if (!nextLoc || !canReassignClinic) return;
    // Guard (2026-09): a roster row with no clinic_name/city would send
    // {name: null, city: null}, which the endpoint reads as an explicit
    // CLEAR — silently unassigning the patient instead of reassigning it.
    // Happens whenever /crm/locations falls back to config/locations.json.
    if (!nextLoc.clinic_name) {
      alert(
        "This clinic is missing its name and city in the roster, so the " +
        "patient's assignment can't be changed to it. Refresh the clinic " +
        "list or pick another clinic."
      );
      return;
    }
    // No-op when the pair already matches (idempotent — backend also handles).
    const sameName = (nextLoc.clinic_name || null) === (patientData?.assigned_clinic || null);
    const sameCity = (nextLoc.city || null) === (patientData?.assigned_clinic_city || null);
    if (sameName && sameCity) return;

    const reasons = _assignedClinicConfirmReasons(nextLoc);
    if (reasons.length === 0) {
      // Same-classification change with no future appointment at current
      // clinic → apply without a prompt (per ticket).
      persistAssignedClinicPair(nextLoc);
      return;
    }
    setAssignedClinicConfirm({ nextLoc, reasons });
  };

  // Item 5 (C2, 2026-09-21) — the front desk's transfer targets come from
  // `GET /crm/clinic-transfer-options`, i.e. the roster rows the server
  // VALIDATES against, so the picker cannot offer a clinic the setter
  // would refuse. Network partners are absent by construction (two
  // layers, both fail-closed).
  // Deliberately NOT narrowed to the user's own codes — handing a patient
  // to the clinic that should have them is the point, and it moves the
  // card out of this queue.
  const [westStarTransferTargets, setWestStarTransferTargets] = useState([]);
  useEffect(() => {
    if (!isWsLocationUser) return;
    let cancelled = false;
    api.get("/crm/clinic-transfer-options")
      .then(r => { if (!cancelled) setWestStarTransferTargets(r.data?.options || []); })
      .catch(() => { if (!cancelled) setWestStarTransferTargets([]); });
    return () => { cancelled = true; };
  }, [isWsLocationUser]);

  // The card left this user's queue: stated explicitly, then the panel
  // closes, so a front-desk user never wonders where the patient went.
  const [transferredAway, setTransferredAway] = useState(null);

  const onTransferDropdownChange = (code) => {
    const target = (westStarTransferTargets || []).find(o => o.location_code === code) || null;
    if (!target) return;
    const nextLoc = {
      id: target.location_code,
      clinic_name: target.name,
      city: target.city,
      is_network: false,
    };
    const sameName = (nextLoc.clinic_name || null) === (patientData?.assigned_clinic || null);
    const sameCity = (nextLoc.city || null) === (patientData?.assigned_clinic_city || null);
    if (sameName && sameCity) return;
    setAssignedClinicConfirm({ nextLoc, reasons: _assignedClinicConfirmReasons(nextLoc) });
  };

  // Fetch appointment history when patient changes
  useEffect(() => {
    if (!patient?.id) return;
    setAppointmentsLoading(true);
    api.get(`/crm/patients/${patient.id}/appointments`)
      .then(r => setAppointments(r.data || []))
      .catch(() => setAppointments([]))
      .finally(() => setAppointmentsLoading(false));
    // P3: asked AFTER the stored list, never instead of it; the panel never
    // waits on it. A card switch aborts it, and an answer is tagged with the
    // patient it was for.
    setLiveVisits(null);
    setCancelRefused(new Set());
    setCancelNotice(null);
    // Option A: only a card the server flagged (live_visits: true, the same
    // rule the endpoint uses) asks; every other card makes no call at all.
    if (patient?.live_visits === true) loadLiveVisits(false);
    return () => { if (liveAbortRef.current) liveAbortRef.current.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient?.id]);

  // P3: the live check. Only the panel (on open), its Refresh, and the re-read
  // after a cancel call it.
  function loadLiveVisits(refresh) {
    if (!patient?.id || patient.live_visits !== true) return;   // Option A: dark cards never call
    if (liveAbortRef.current) liveAbortRef.current.abort();
    const ctrl = new AbortController();
    liveAbortRef.current = ctrl;
    const pid = patient.id;
    const cached = refresh ? null : liveCacheGet(pid);
    if (cached) { setLiveVisits(cached); return; }   // same patient again within 5 minutes: no new call
    setLiveChecking(true);
    api.get(`/crm/patients/${pid}/live-visits`, { params: refresh ? { refresh: true } : undefined, signal: ctrl.signal })
      .then(r => {
        if (ctrl.signal.aborted || !r.data) return;
        const tagged = { ...r.data, forPatientId: pid };
        if (tagged.enabled && tagged.status === "ok") liveCacheSet(pid, tagged);
        setLiveVisits(tagged);
      })
      .catch(() => {
        if (ctrl.signal.aborted) return;
        // W3 item 8: a request error shows "Couldn't check Raintree" in the panel (the card's
        // stored visits without states), never the old list or an earlier answer's rows
        setLiveVisits({ enabled: true, status: "error", rows: null, checked_at: null, forPatientId: pid });
      })
      .finally(() => { if (liveAbortRef.current === ctrl) setLiveChecking(false); });
  }

  // Fetch latest non-cancelled Network Scheduling request for this patient
  // (powers the small status badge in the detail panel + serves as the
  //  "any active scheduling?" gate). Null when the patient has nothing in
  // flight so the badge stays hidden and the panel doesn't pick up noise.
  const [schedulingStatus, setSchedulingStatus] = useState(null);
  const hasOpenPick = !!patient?.patient_pick;
  useEffect(() => {
    // Network-lane data (Commit 2, 2026-09): a WS Location user can neither
    // lock in nor counter a clinic's proposed times — every one of those
    // endpoints denies the role. So the badge would be a dead-end
    // invitation; don't render it and don't even fetch the request.
    // PR #9 (2026-10-05): the ONE exception is a patient's own open pick
    // (the list row's `patient_pick`): the clinic CAN act on it by booking
    // in the CRM, so it is fetched and shown, and nothing else is.
    if (isWsLocationUser && !hasOpenPick) {
      setSchedulingStatus(null);
      return;
    }
    if (!patient?.id) {
      setSchedulingStatus(null);
      return;
    }
    let cancelled = false;
    api.get(`/scheduling/patients/${patient.id}/active-request`)
      .then(r => {
        if (cancelled) return;
        const req = r.data?.request || null;
        setSchedulingStatus(isWsLocationUser && !isPatientPickRequest(req) ? null : req);
      })
      .catch(() => { if (!cancelled) setSchedulingStatus(null); });
    return () => { cancelled = true; };
  }, [patient?.id, isWsLocationUser, hasOpenPick]);

  // Filter locations by patient's therapy type
  const ownBookingLocations = useMemo(() => {
    if (!locations?.length) return [];
    
    const patientTherapyType = (patient.therapy_type || "").toLowerCase().trim();
    
    // If no therapy type specified, show all locations
    if (!patientTherapyType) return locations;
    
    // Determine patient's therapy category
    const needsPT = patientTherapyType.includes("physical") || patientTherapyType === "pt";
    const needsAcupuncture = patientTherapyType.includes("acupuncture") || patientTherapyType.includes("acu");
    const needsAqua = patientTherapyType.includes("aqua");
    
    // Filter locations by therapy type field from BigQuery
    const filtered = locations.filter(loc => {
      const locTherapy = (loc.therapy_type || "").toLowerCase().trim();
      
      if (needsPT) {
        return locTherapy.includes("physical") || locTherapy === "pt";
      } else if (needsAcupuncture) {
        return locTherapy.includes("acupuncture");
      } else if (needsAqua) {
        return locTherapy.includes("aqua");
      }
      
      // Default: show all
      return true;
    });
    
    // If filtering resulted in no locations, fall back to all
    const byTherapy = filtered.length > 0 ? filtered : locations;

    // WS Location users book only at their OWN sites (Commit 2, 2026-09) —
    // the server refuses any other target, so offering one would be a lie.
    // FAIL CLOSED: if their codes are known but no row carries a matching
    // `raintree_code` (the roster fell back to config/locations.json, which
    // has no codes), offer NOTHING and say why, rather than a list of sites
    // the server will refuse.
    if (!isWsLocationUser) return byTherapy;
    if (!myLocationCodes?.length) return [];
    return byTherapy.filter(l => myLocationCodes.includes(l.raintree_code));
  }, [locations, patient.therapy_type, isWsLocationUser, myLocationCodes]);

  // clinic-telehealth-book-at: a clinic login in the enabled cohort may also
  // BOOK at Telehealth. The server decides (`booking_extra_codes`); this only
  // appends that row after the login's own sites. Booking there never moves
  // the card: the dropdown writes the assignment for sales/admin only
  // (`canReassignClinic`), and Telehealth stays out of "Transfer to".
  // No extra codes: the same array as before, for every role.
  const filteredLocations = useMemo(
    () => (isWsLocationUser
      ? appendBookingExtras(ownBookingLocations, locations, myExtraBookingCodes)
      : ownBookingLocations),
    [ownBookingLocations, locations, myExtraBookingCodes, isWsLocationUser]);

  // Prefill the clinic dropdown (2026-08-25). Priority 0 is the patient's
  // CURRENT ASSIGNMENT — before this, the dropdown only ever showed a
  // recommendation (raintree code → city → first option), so an
  // already-assigned patient opened on the wrong clinic. Assignment
  // mechanics are untouched; this is display state only. Reassignment
  // updates `patientData.assigned_clinic` locally via the paired-writer
  // handler, so this re-resolves to the new clinic immediately and again
  // on every re-open. Matching lives in
  // lib/crmPatientModal.js so it can be unit-tested.
  const [locationMatchType, setLocationMatchType] = useState(null); // "assigned" | "code" | "city" | "fallback" | "manual"

  // Skip-with-reason (Commit B, §11) was REMOVED from queue mode by Ticket 1
  // (2026-09-05): lock-in means there is no skip escape. The endpoint and the
  // recorded history remain — see the note on SKIP_REASON_OPTIONS above.
  //
  // Lock-in gate. Only queue mode locks; a patient opened from the list
  // (`isDirectView`) was never served, so Next stays free there — managers
  // keep full navigation. `actionSaved` is keyed to the served patient id by
  // the parent, so a fresh serve is locked again with no reset step.
  const nextLocked = !isDirectView && !actionSaved;

  useEffect(() => {
    // Prefill from the login's own sites only: an extra site (Telehealth) is
    // picked by hand, never opened on by a recommendation code.
    if (!ownBookingLocations?.length) return;
    const { location, matchType } = pickPrefilledLocation({
      locations: ownBookingLocations,
      assignedClinic: patientData?.assigned_clinic,
      assignedCity: patientData?.assigned_clinic_city,
      raintreeCode: patient.raintree_location_code,
      patientCity: patient.patient_city,
    });
    setSelectedLocation(location);
    setLocationMatchType(matchType);
  }, [patient.id, patient.raintree_location_code, patient.patient_city,
      patientData?.assigned_clinic, patientData?.assigned_clinic_city,
      ownBookingLocations]);

  const saveNotes = async () => {
    setNotesSaving(true);
    try {
      const res = await api.put(`/crm/patients/${patient.id}/notes`, { notes });
      setNotesSaved(true);
      setNotesTimestamp({ at: res.data.notes_updated_at, by: res.data.notes_updated_by });
      onNotesSaved?.(notes);
      setTimeout(() => setNotesSaved(false), 2500);
    } catch (err) {
      console.error("Failed to save notes:", err);
    } finally {
      setNotesSaving(false);
    }
  };

  // Cancel an appointment. 2026-10-21 (card 21575): always asks first, and says what the patient was told.
  // W3 item 8: a card the server flagged shows the panel at once (stored rows,
  // "Checking Raintree…"); once an answer arrives, its `enabled` decides.
  const liveOn = liveVisits ? !!liveVisits.enabled : patient?.live_visits === true;
  const liveMine = !!(liveOn && patient && liveVisits && liveVisits.forPatientId === patient.id);
  const liveRows = liveMine ? liveRowsFor(liveVisits, appointments, cancelRefused, isWallClockPast) : null;

  // P3: at click time, the panel acts only for the card's own patient: a live
  // row needs the answer to be this patient's; a fallback row must be one of
  // this card's stored visits.
  function panelActsFor(row) {
    if (!patient?.id || row?.appointment_id == null) return false;
    if (liveRows) return liveVisits?.forPatientId === patient.id;
    return appointments.some(a => a.id === row.appointment_id);
  }

  // P3 (live panel only): after a cancel, drop this referral's prefetched card
  // and re-read Raintree past the cache, so the panel shows what Raintree holds.
  function afterLiveCancel() {
    if (patient?.bq_file_id) clearCardPrefetch(patient.bq_file_id);
    liveCacheDrop(patient?.id);
    loadLiveVisits(true);
  }

  const cancelAppointment = async (appt, isPast) => {
    const apptId = appt.id;
    if (!window.confirm(cancelPromptText(appt, isPast))) return;
    const cancelDay = formatWallClockDate(appt.appointment_datetime, { month: "short", day: "numeric" }); // P3 toast
    console.log("Cancel clicked for appointment:", apptId);
    setCancellingApptId(apptId);
    try {
      const res = await api.delete(`/crm/appointments/${apptId}`);
      console.log("Appointment cancelled successfully");
      setAppointments(prev => prev.filter(a => a.id !== apptId));
      // P3 (live panel only): a refusal (today's 200 + raintree_error) shows ONE
      // message and keeps the visit showing; never #51's success or text line.
      const out = liveOn ? cancelOutcome(res?.status ?? 200, res?.data, cancelDay, isPast) : null;
      if (out && out.refused) {
        setCancelRefused(prev => new Set([...prev, apptId]));
        setCancelNotice({ message: out.message, description: out.description, forPatientId: patient?.id });
      } else {
        if (liveOn) setCancelNotice(null);   // P3: a clean cancel clears an earlier refusal message
        // 2026-10-23 (Matt's review of #51): say what actually happened about the patient's text
        const said = cancelResultText(res && res.data && res.data.patient_text);
        if (said) (said.warn ? sonnerToast.warning : sonnerToast.success)(said.text, { duration: said.warn ? 10000 : 5000 });
      }
      if (liveOn) afterLiveCancel();
    } catch (err) {
      console.error("Cancel failed:", err);
      if (liveOn) {
        // P3: any non-2xx (the refused-cancel PR's 409 / 502, or anything else): one message.
        const out = cancelOutcome(err?.response?.status ?? 0, err?.response?.data, cancelDay, isPast);
        setCancelNotice({ message: out.message, description: out.description, forPatientId: patient?.id });
        afterLiveCancel();
      } else {
        // W3: the routes' 409 / 502 keep the visit; say so in cancelOutcome's words (P3 off too).
        const code = err?.response?.data?.detail?.code;
        if (code === "raintree_refused" || code === "raintree_not_found" || code === "raintree_unavailable") {
          const out = cancelOutcome(err.response.status, err.response.data, cancelDay, isPast);
          alert(out.description ? `${out.message}\n\n${out.description}` : out.message);
        } else {
          alert(formatApiError(err, "Failed to cancel appointment"));
        }
      }
    } finally {
      setCancellingApptId(null);
    }
  };

  // Open reschedule modal for specific appointment
  const openReschedule = (appt) => {
    setRescheduleApptId(appt.id);
    // P4 part 2: the visit's own clinic and type decide move vs cancel + book.
    setRescheduleFrom({ location_id: appt.location_id, appointment_type: appt.appointment_type });
    // Pre-select the location
    const loc = locations.find(l => l.name === appt.location_name || l.id === appt.location_id);
    if (loc) setSelectedLocation(loc);
    setShowSchedule(true);
  };

  // Save callback/follow-up time
  const saveCallback = async () => {
    if (!callbackDate || !callbackTime) return;
    setCallbackSaving(true);
    try {
      const callbackAt = new Date(`${callbackDate}T${callbackTime}`).toISOString();
      await api.put(`/crm/patients/${patient.id}/callback`, {
        callback_at: callbackAt,
        callback_reason: callbackReason,
      });
      setShowCallbackModal(false);
      setCallbackDate("");
      setCallbackTime("");
      setCallbackReason("");
      // Ticket 1: a saved callback IS a qualifying action, so it unlocks Next
      // instead of releasing the patient behind the rep's back. The rep stays
      // on the record they just worked and advances deliberately.
      onActionRecorded?.();
    } catch (err) {
      alert(formatApiError(err, "Failed to set callback"));
    } finally {
      setCallbackSaving(false);
    }
  };

  // Clear existing callback
  const clearCallback = async () => {
    try {
      await api.delete(`/crm/patients/${patient.id}/callback`);
    } catch (err) {
      console.error("Failed to clear callback:", err);
    }
  };

  // Format relative timestamp
  const formatRelativeTime = (isoString) => {
    if (!isoString) return null;
    const diff = Date.now() - new Date(isoString).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  const hasAppointment = !!patient.appointment_datetime;

  if (!patient) return null;

  const address = [patient.patient_address, patient.patient_city, patient.patient_state]
    .filter(Boolean).join(", ");

  const handleBooked = (data) => {
    // W3 item 5: the old visit's day, read BEFORE the list refreshes (a reschedule keeps the row id).
    const orphaned = !!data?.appointment?.orphaned_raintree_appointment_id;
    const oldAppt = orphaned && rescheduleApptId != null ? appointments.find(a => a.id === rescheduleApptId) : null;
    const oldDay = oldAppt ? formatWallClockDate(oldAppt.appointment_datetime, { month: "short", day: "numeric" }) : null;
    setShowSchedule(false);
    setRescheduleApptId(null);  // Clear reschedule state
    // Refresh appointments list — the canonical purple
    // "Scheduled Appointments" section below renders this array, so
    // a fresh fetch is what surfaces the newly-booked appointment to
    // the user. (The legacy blue "Appointment Scheduled" banner that
    // used to render `bookedInfo` was removed 2026-06-02 — it was
    // visually redundant with this section and showed the time
    // BEFORE the TIMESTAMPTZ round-trip, which masked a real
    // timezone-display bug in the canonical list. See lib/wallClock.js
    // below for the fix.)
    api.get(`/crm/patients/${patient.id}/appointments`)
      .then(r => setAppointments(r.data || []))
      .catch(() => {});
    // P3 (live panel only): after a booking or a reschedule, like after a
    // cancel, drop this patient's browser cache and re-read past the server's.
    if (liveOn) { liveCacheDrop(patient.id); loadLiveVisits(true); }
    onAction("__appointment_booked__", orphaned ? { ...data, old_visit_day: oldDay } : data);
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(15,23,42,0.55)", backdropFilter: "blur(4px)" }}>
        <div
          data-testid="queue-banner"
          className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl border border-slate-100 overflow-hidden"
          style={{ animation: "slideUp 0.25s ease-out", maxHeight: "92vh", overflowY: "auto" }}
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-slate-100 bg-slate-50">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-lg font-bold">
                {(patient.first_name?.[0] || "?").toUpperCase()}
              </div>
              <div>
                <div className="flex items-baseline gap-2 flex-wrap">
                  <p className="text-lg font-bold text-slate-900" data-testid="banner-patient-name">
                    {[patientData.first_name, patientData.last_name]
                      .filter(Boolean).join(" ") || patient.full_name || "Unknown Patient"}
                  </p>
                  {/* 2026-08-25: name is editable like any other field.
                      PG columns crm_patients.first_name / .last_name are
                      already on EDITABLE_PATIENT_FIELDS (crm_router L1625),
                      so this rides the same PUT /api/crm/patients/{id}/fields
                      path — audit-logged, BQ write-back, and stamped into
                      locally_modified_fields so the 60s sync can't revert it.
                      Two fields rather than one split string: the columns are
                      separate and splitting free text guesses at names. */}
                  <span
                    className="text-xs text-slate-500 font-mono whitespace-nowrap"
                    data-testid="banner-patient-dob"
                  >
                    DOB: {formatDOB(patient.date_of_birth)}
                  </span>
                  {/* Item 1 (2026-09-20) — the Raintree patient number, in
                      the header for every role, visible without expanding
                      a section, and shown EXACTLY once (the editable copy
                      in Case & Insurance Details was removed). Corrections
                      live on the Referral Intake tab, which writes
                      BigQuery; the card's edit path writes Postgres only
                      and would leave the two stores disagreeing. */}
                  <span
                    className="text-xs text-slate-600 font-mono whitespace-nowrap px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200"
                    data-testid="banner-patient-pn"
                    title="Raintree patient number — correct it on the Referral Intake tab"
                  >
                    PN: {patient.raintree_patient_number || "—"}
                  </span>
                  {/* Item 8 (2026-09-20) — post-op chip. Upstream writes
                      'Yes' or empty, never 'No', so the server hands us a
                      bool. Display only in this commit; the queue ordering
                      half ships in Commit C. */}
                  {patient.post_op && (
                    <span
                      data-testid="banner-post-op-chip"
                      title={patient.post_op_source ? `Post-op (source: ${patient.post_op_source})` : "Post-op"}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-violet-100 text-violet-800 border border-violet-200 whitespace-nowrap"
                    >
                      Post-op
                    </span>
                  )}
                  {/* Consent chip (Contract §5 / SMS Commit 2). Shown when
                      consent is empty OR false, because both mean "not
                      textable": empty is unknown, false is a STOP we must
                      never overwrite. The prompt tells staff the one place
                      consent can be captured. */}
                  {!patient.sms_consent && (
                    <span
                      data-testid="sms-no-consent-chip"
                      title={patient.sms_consent === false
                        ? "This patient replied STOP. Only the patient can restart texts, by replying START."
                        : "Ask the patient for permission to text, then record it under Patient Phone below. Consent is never assumed."}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-600 border border-slate-200 whitespace-nowrap"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                      No SMS consent on record
                    </span>
                  )}
                </div>
                {/* Name editors live on their OWN row: in the baseline row
                    the inputs + inline error stretched the header and
                    pushed the Send-Confirmation controls off the modal
                    edge. Fixed-width columns keep the header stable. */}
                <div
                  className="mt-1 grid grid-cols-2 gap-4 max-w-sm"
                  data-testid="banner-name-editors"
                >
                  <EditableField
                    label="First name" value={patientData.first_name}
                    fieldName="first_name" patientId={patient.id}
                    onSave={handleSaveField} canEdit={canEditField("first_name")} required
                  />
                  <EditableField
                    label="Last name" value={patientData.last_name}
                    fieldName="last_name" patientId={patient.id}
                    onSave={handleSaveField} canEdit={canEditField("last_name")} required
                  />
                </div>
                <StageBadge stageId={patient.current_stage_id} stageName={patient.stage_name} />
                <IntakeTypeBadge type={patient.intake_type} />
                <Icd10ReviewBadge show={patient.icd10_review} />
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {isDirectView ? (
                <span className="flex items-center gap-1 text-xs text-blue-600 bg-blue-50 border border-blue-200 px-2 py-1 rounded-full font-medium">
                  <User size={11} />
                  Viewing
                </span>
              ) : (
                <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 border border-amber-200 px-2 py-1 rounded-full font-medium">
                  <Clock size={11} />
                  {formatTime(patient.hours_in_stage)} in stage
                </span>
              )}
              <button
                data-testid="banner-release-btn"
                onClick={onRelease}
                disabled={acting || writing}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                title="Close"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {readOnly && (
            <LockBanner
              lockedBy={lockedBy}
              lockInfo={lockInfo}
              onTakeover={onTakeover}
            />
          )}

          {/* Why this patient (Commit B, §7/§10). Rendered VERBATIM from the
              serve payload — the frontend never recomputes priority. Queue
              mode only: a patient opened from the list/search was not
              served by the engine, so there is nothing to explain. */}
          {!isDirectView && serveReason && (
            <div
              data-testid="queue-serve-reason"
              className="mx-6 mt-4 flex items-start gap-2.5 px-3.5 py-2.5 rounded-xl bg-indigo-50 border border-indigo-200"
            >
              <Target size={15} className="text-indigo-600 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-wide text-indigo-500 font-semibold">
                  {/* A re-serve renders the marker's SNAPSHOT, so it is
                      labelled as of serve time — otherwise "overdue by 6
                      days" reads as current urgency long after the work
                      was done. */}
                  {serveReasonIsSnapshot ? "Why this patient — when served" : "Why this patient"}
                </p>
                <p className="text-sm font-medium text-indigo-900" data-testid="queue-serve-reason-text">
                  {serveReason}
                </p>
              </div>
            </div>
          )}

          {/* Patient details grid */}
          <div className="px-6 py-5 grid grid-cols-2 gap-x-8 gap-y-4">
            {/* Phone — prominent, inline-editable. The tel: link wraps the
                READ-ONLY display only; the pencil and input live underneath
                so a Save click never accidentally triggers a phone dial. */}
            <div className="col-span-2">
              <div
                data-testid="banner-phone"
                className="flex items-center gap-3 p-3 rounded-xl bg-emerald-50 border border-emerald-200 group"
              >
                <a
                  href={patient.phone ? `tel:${patient.phone}` : undefined}
                  className="w-9 h-9 rounded-full bg-emerald-600 flex items-center justify-center flex-shrink-0 hover:bg-emerald-700 transition-colors"
                  onClick={(e) => { if (!patient.phone) e.preventDefault(); }}
                >
                  <Phone size={16} className="text-white" />
                </a>
                <div className="min-w-0 flex-1">
                  <EditableField
                    label="Patient Phone"
                    value={patientData.phone}
                    fieldName="phone"
                    patientId={patient.id}
                    onSave={handleSaveField}
                    canEdit={canEditField("phone")}
                    placeholder="No phone on file"
                  />
                </div>
              </div>
              <SmsConsentPanel
                patient={patient}
                readOnly={readOnly}
                onRecorded={(updates) => { if (onPatientUpdated) onPatientUpdated(patient.id, updates); }}
              />
              {/* Wave 5 (2026-10-06): Text history (P-8) -- loads on first open. */}
              <TextHistoryPanel patient={patient} userRole={readOnly ? null : user?.role} />
              {/* 2026-10-20 (esign_tracking): who has signed what -- loads on first open. */}
              <EsignPanel patient={patient} />
            </div>

            {/* Item 4 (C2, 2026-09-21) — DOB joins the front desk's
                correctable group. It was display-only (the header chip)
                until now, and it is the key staff match patients on, so
                the editor asks for an explicit confirmation first. */}
            <div className="col-span-2" data-testid="dob-edit-group">
              <EditableField
                icon={CalendarDays}
                label="Date of Birth"
                value={patientData.date_of_birth}
                fieldName="date_of_birth"
                patientId={patient.id}
                onSave={handleSaveDob}
                canEdit={canEditField("date_of_birth")}
                type="date"
                emptyLabel="missing"
                emptyWarning
              />
            </div>

            {/* ── WORKING BLOCK (2026-08-25 restructure) ──────────────
                Everything that used to live BELOW View Documents,
                moved here as ONE unit in its original internal order:
                purple appointments banner · clinic dropdown · status ·
                Set Callback · date range + Schedule · Schedule Network
                Clinic · Patient Notes. ALWAYS OPEN — never collapsible.
                Functionality is byte-identical; only position moved. */}
            <div className="col-span-2 space-y-3" data-testid="crm-working-block">
            {/* 2026-06-02: Removed the legacy blue "Appointment Scheduled"
                banner that lived here. It rendered the just-booked
                appointment (driven by transient `bookedInfo` state) with
                its own Reschedule + Cancel buttons that called the same
                handlers as the canonical purple section below. Pure
                visual duplication. The blue banner also hid a real
                timezone-display bug — see lib/wallClock.js and the
                inline comment in the Appointment History section below. */}

            {/* Appointment History Section */}
            {liveOn ? (
              <CardVisitsPanel
                live={liveVisits}
                rows={liveRows}
                stored={appointments}
                checking={liveChecking}
                actionsAllowed={liveMine}
                clinicFor={(row) => {
                  const loc = locations.find(l => l.name === row.location_name || l.id === row.location_id);
                  return { name: row.location_name || loc?.name || null, address: loc?.address || null, phone: loc?.phone || null };
                }}
                onRefresh={() => loadLiveVisits(true)}
                onReschedule={(row) => { if (panelActsFor(row)) openReschedule({ ...row, id: row.appointment_id ?? row.id }); }}
                onCancel={(row, isPast) => { if (panelActsFor(row)) cancelAppointment({ ...row, id: row.appointment_id ?? row.id }, isPast); }}
                cancellingApptId={cancellingApptId}
                notice={cancelNotice && cancelNotice.forPatientId === patient?.id ? cancelNotice : null}
                onDismissNotice={() => setCancelNotice(null)}
              />
            ) : (appointments.length > 0 || appointmentsLoading) && (
              <div className="col-span-2 p-3 rounded-xl bg-purple-50 border border-purple-200" data-testid="appointments-banner">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <History size={14} className="text-purple-600" />
                    <p className="text-xs font-semibold text-purple-700 uppercase tracking-wide">
                      Scheduled Appointments ({appointments.length})
                    </p>
                  </div>
                </div>
                {appointmentsLoading ? (
                  <div className="flex items-center gap-2 text-xs text-purple-500">
                    <span className="w-3 h-3 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                    Loading...
                  </div>
                ) : (
                  <div className="space-y-2">
                    {appointments.map((appt, idx) => {
                      // 2026-06-02 — Timezone display fix.
                      //
                      // `appt.appointment_datetime` round-trips through
                      // PostgreSQL TIMESTAMPTZ (DateTime(timezone=True)
                      // in models.py). When the user picks 10:00 AM the
                      // frontend sends a naive ISO string; asyncpg
                      // interprets naive datetimes as UTC on insert,
                      // and on read-back `.isoformat()` produces
                      // "2026-03-15T10:00:00+00:00". `new Date(...)` of
                      // that string parses as UTC and `.toLocaleTimeString`
                      // converts to the browser's local zone, so a
                      // 10:00 AM picked-time would render as 4:00 AM
                      // CST / 3:00 AM PST. The legacy blue banner above
                      // (now removed) bypassed this only because the
                      // backend pre-formatted a `display` string from the
                      // in-memory naive datetime BEFORE the DB write —
                      // accidentally correct.
                      //
                      // Fix: strip any timezone suffix (`Z` or `±HH:MM`)
                      // before parsing so JS treats the wall-clock
                      // exactly as the staff member picked it. This is
                      // the right semantics for clinic appointments —
                      // "10 AM at the clinic" stays "10 AM" regardless
                      // of who's viewing.
                      const apptDate = parseWallClock(appt.appointment_datetime);
                      const isPast = isWallClockPast(appt.appointment_datetime);
                      const isCancelling = cancellingApptId === appt.id;
                      const loc = locations.find(l => l.name === appt.location_name || l.id === appt.location_id);
                      return (
                        <div
                          key={appt.id || idx}
                          data-testid={`appt-history-${idx}`}
                          className={`flex items-center justify-between p-2 rounded-lg ${isPast ? "bg-purple-100/50" : "bg-white border border-purple-200"}`}
                        >
                          <div className="flex items-center gap-2">
                            <CalendarCheck size={12} className={isPast ? "text-purple-400" : "text-purple-600"} />
                            <div>
                              <p className={`text-xs font-medium ${isPast ? "text-purple-500" : "text-purple-800"}`}>
                                {appt.location_name}
                              </p>
                              {loc?.address && (
                                <p className={`text-xs ${isPast ? "text-purple-400" : "text-purple-500"}`}>
                                  {loc.address}
                                </p>
                              )}
                              {loc?.phone && (
                                <a href={`tel:${loc.phone}`} className={`text-xs ${isPast ? "text-purple-400" : "text-purple-500"} hover:underline font-mono`}>
                                  {loc.phone}
                                </a>
                              )}
                              <p className={`text-xs ${isPast ? "text-purple-400" : "text-purple-600"}`}>
                                {formatWallClockDate(appt.appointment_datetime)}
                                {apptDate && ` at ${formatWallClockTime(appt.appointment_datetime)}`}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5">
                            {appt.reminder_sent_at && (
                              <span className="text-xs text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded font-medium">Reminded</span>
                            )}
                            <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${isPast ? "bg-slate-100 text-slate-500" : "bg-purple-200 text-purple-700"}`}>
                              {isPast ? "Past" : "Upcoming"}
                            </span>
                            {/* Reschedule & Cancel buttons - show for all appointments */}
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); e.preventDefault(); openReschedule(appt); }}
                              className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors font-medium"
                              data-testid={`reschedule-appt-${appt.id}`}
                            >
                              Reschedule
                            </button>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); e.preventDefault(); cancelAppointment(appt, isPast); }}
                              disabled={isCancelling}
                              className="text-xs px-2 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200 transition-colors font-medium disabled:opacity-50"
                              data-testid={`cancel-appt-${appt.id}`}
                            >
                              {isCancelling ? "..." : "Cancel"}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          {/* Scheduling controls */}
          <div className="space-y-3" data-testid="scheduling-controls">
            {/* Location dropdown. For WS Location users this is a BOOKING
                selector, not an assignment control: their choice is
                constrained to their own sites and never persists an
                assignment (that endpoint stays sales/admin). */}
            <div className="flex items-center gap-2">
              <MapPin size={14} className="text-slate-400 flex-shrink-0" />
              {isWsLocationUser && (
                <span className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold whitespace-nowrap" data-testid="book-at-label">
                  Book at
                </span>
              )}
              <select
                data-testid="location-dropdown"
                value={selectedLocation?.id || ""}
                onChange={e => onLocationDropdownChange(e.target.value)}
                disabled={assignedClinicSaving}
                className="flex-1 h-9 px-3 rounded-xl border border-slate-200 bg-white text-sm text-slate-700 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-60"
              >
                {(filteredLocations || []).map(l => (
                  <option key={l.id} value={l.id}>{l.name}{l.therapy_type ? ` (${l.therapy_type})` : ""}</option>
                ))}
                {isWsLocationUser && (filteredLocations || []).length === 0 && (
                  <option value="">Ask WestStar staff to book this one</option>
                )}
              </select>
              {isWsLocationUser && (filteredLocations || []).length === 0 && (
                <span className="text-xs text-amber-700 whitespace-nowrap" data-testid="no-bookable-location">
                  Clinic list unavailable
                </span>
              )}
              {patient.therapy_type && (
                <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded font-medium whitespace-nowrap">
                  {patient.therapy_type}
                </span>
              )}
              {locationMatchType === "assigned" && (
                <span className="text-xs text-blue-600 font-medium whitespace-nowrap flex items-center gap-1" data-testid="location-assigned-chip">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500 inline-block" />
                  Assigned
                </span>
              )}
              {locationMatchType === "code" && (
                <span className="text-xs text-emerald-600 font-medium whitespace-nowrap flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                  Recommended
                </span>
              )}
              {locationMatchType === "city" && (
                <span className="text-xs text-amber-600 font-medium whitespace-nowrap flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" />
                  Nearest City
                </span>
              )}
            </div>

            {/* Item 5 (C2, 2026-09-21) — the front desk's TRANSFER control.
                Separate from "Book at" above on purpose: booking is
                constrained to their OWN sites, while a transfer is how a
                patient reaches the clinic that should have them — which
                means it can move the card OUT of this user's queue, and
                that is the point.

                Network partner clinics are never listed (the server also
                refuses them, fail-closed); the options are the
                WestStar-owned sites the roster publishes. */}
            {isWsLocationUser && (
              <div className="flex items-center gap-2" data-testid="fd-transfer-row">
                <Building2 size={14} className="text-slate-400 flex-shrink-0" />
                <span className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold whitespace-nowrap" data-testid="fd-transfer-label">
                  Transfer to
                </span>
                <select
                  data-testid="fd-transfer-dropdown"
                  value=""
                  disabled={assignedClinicSaving || readOnly}
                  onChange={e => onTransferDropdownChange(e.target.value)}
                  className="flex-1 h-9 px-3 rounded-xl border border-slate-200 bg-white text-sm text-slate-700 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-60"
                >
                  <option value="">
                    {westStarTransferTargets.length
                      ? "Choose a WestStar clinic…"
                      : "Clinic list unavailable"}
                  </option>
                  {westStarTransferTargets.map(l => (
                    <option key={l.location_code} value={l.location_code}>
                      {l.label}{l.therapy_type ? ` (${l.therapy_type})` : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Action row: Status | Set Callback | [date-from] → [date-to] | Schedule */}
            <div className="flex items-center gap-2 flex-wrap">
              {/* Unified Status dropdown — replaces the legacy
                  "Didn't Answer" / "Ready to Schedule" buttons. Triggers
                  onAction("__set_stage__", stageId) so the parent can fold
                  this into the same stage-update flow used by the row. */}
              <div className="h-10 flex items-center px-2 rounded-xl border border-slate-200 bg-white" data-testid="banner-status-wrapper">
                <span className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mr-2">Status</span>
                <StageDropdown
                  size="lg"
                  patientId={`banner-${patient.id}`}
                  currentStageId={patient.current_stage_id}
                  onSelect={(sid) => onAction("__set_stage__", { stage_id: sid })}
                />
              </div>
              <button
                data-testid="banner-set-callback"
                onClick={() => setShowCallbackModal(true)}
                disabled={acting}
                className="h-10 flex items-center justify-center gap-1.5 px-4 rounded-xl border-2 border-amber-400 bg-amber-50 text-amber-700 font-semibold text-sm hover:bg-amber-100 hover:border-amber-500 disabled:opacity-50 transition-all"
              >
                <Clock size={14} />
                Set Callback
              </button>

              {/* Date range */}
              <div className="flex items-center gap-1.5 ml-auto">
                <CalendarDays size={14} className="text-slate-400 flex-shrink-0" />
                <input
                  data-testid="date-start"
                  type="date"
                  value={dateStart}
                  min={today}
                  onChange={e => setDateStart(e.target.value)}
                  className="h-9 px-2 rounded-xl border border-slate-200 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-slate-400 text-xs font-medium">→</span>
                <input
                  data-testid="date-end"
                  type="date"
                  value={dateEnd}
                  min={dateStart}
                  onChange={e => setDateEnd(e.target.value)}
                  className="h-9 px-2 rounded-xl border border-slate-200 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  data-testid="schedule-btn"
                  onClick={() => setShowSchedule(true)}
                  disabled={!selectedLocation || !dateStart || !dateEnd || readOnly}
                  className="h-10 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm disabled:opacity-50 transition-colors shadow-sm flex items-center gap-1.5"
                >
                  <CalendarCheck size={14} />
                  {appointments.length > 0 ? "Add Appointment" : "Schedule"}
                </button>
                {/* Network-clinic patients get a parallel CTA that bounces
                    them to the Network Scheduling page with the relevant
                    fields pre-filled. WS-owned clinics still use the
                    existing Raintree-backed Schedule button above.
                    WS Location users never see this lane — the network
                    endpoints deny their role server-side (2026-09). */}
                {selectedLocation?.is_network && user?.role !== "location" && (
                  <button
                    type="button"
                    data-testid="schedule-network-btn"
                    onClick={() => {
                      const params = new URLSearchParams();
                      params.set("patient_id", String(patient.id));
                      const fullName = [patient.first_name, patient.last_name].filter(Boolean).join(" ").trim();
                      if (fullName) params.set("patient_name", fullName);
                      if (selectedLocation?.id) params.set("location_code", selectedLocation.id);
                      // Map therapy_type → appt code. Default PTEVA for
                      // first-time scheduling; staff can flip to PTRA in the
                      // pre-filled modal.
                      params.set("appointment_type", "PTEVA");
                      if (patientData.case_type) params.set("case_type", patientData.case_type);
                      if (patientData.body_parts_involved) params.set("body_parts", patientData.body_parts_involved);
                      // Land in Calendar view with a propose session active
                      // (two-click ranges) instead of the List-view modal.
                      params.set("view", "calendar");
                      navigate(`/network-scheduling?${params.toString()}`);
                    }}
                    disabled={!selectedLocation}
                    className="h-10 px-4 rounded-xl bg-teal-600 hover:bg-teal-700 text-white font-semibold text-sm disabled:opacity-50 transition-colors shadow-sm flex items-center gap-1.5"
                    title="Send proposed times to this partner clinic"
                  >
                    <Send size={14} />
                    Schedule at Network Clinic
                  </button>
                )}
              </div>
            </div>

            {/* Network Scheduling status badge — only renders when this
                patient has a non-cancelled scheduling request in flight.
                Hidden entirely otherwise to keep the panel clean. */}
            {schedulingStatus && (
              <SchedulingStatusBadge
                request={schedulingStatus}
                locations={locations}
              />
            )}

            {/* Telehealth checkbox (2026-10-07): the patient's answer to Geoff's
                box on the send-your-times page. Willingness only; the telehealth
                team takes consent when it books. */}
            {patient.telehealth && (
              <div
                data-testid="crm-panel-telehealth"
                className={`p-3 rounded-xl border text-sm ${
                  patient.telehealth.ok
                    ? "bg-violet-50 border-violet-200 text-violet-900"
                    : "bg-slate-50 border-slate-200 text-slate-700"
                }`}
              >
                <span className="font-semibold">
                  {patient.telehealth.ok ? "Telehealth OK" : "Telehealth: no"}
                </span>
                {" · "}
                {patient.telehealth.ok
                  ? "patient is open to telehealth if no in-person visit within 3 business days"
                  : "patient unticked the telehealth box"}
                {patient.telehealth.answered_at && (
                  <span className="text-xs opacity-75">
                    {" · sent "}
                    {new Date(patient.telehealth.answered_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                  </span>
                )}
                {patient.telehealth.on_list && (
                  <div className="mt-1 font-semibold">
                    Next step: Book a telehealth evaluation (THEVA) at the Telehealth location (00020)
                    within 1 business day. Keep any in-person visit as booked.
                  </div>
                )}
              </div>
            )}
            
            {/* Existing callback banner. The server sends the ONE liveness
                answer (`callback_state`, from queue_engine.callback_state),
                so the banner, the why-served line and the engine rank can
                never disagree again: pending (future) and live (due, not yet
                acted on) show; consumed shows nowhere. */}
            {patient.callback_at &&
             (patient.callback_state === "pending" || patient.callback_state === "live") && (
              <div
                data-testid="callback-banner"
                className="flex items-center justify-between gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200"
              >
                <div className="flex items-center gap-2">
                  <Clock size={16} className="text-amber-600" />
                  <div>
                    <p className="text-sm font-semibold text-amber-800">
                      {patient.callback_state === "live" ? "Callback Due" : "Callback Scheduled"}
                    </p>
                    <p className="text-xs text-amber-600">
                      {new Date(patient.callback_at).toLocaleString()}
                      {patient.callback_reason && ` — ${patient.callback_reason}`}
                    </p>
                  </div>
                </div>
                <button
                  onClick={clearCallback}
                  className="text-xs px-2 py-1 rounded bg-amber-200 text-amber-800 hover:bg-amber-300 transition-colors font-medium"
                >
                  Clear
                </button>
              </div>
            )}
          </div>

          {/* Notes section */}
          <div className="border-t border-slate-100 pt-4" data-testid="notes-block">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide flex items-center gap-1.5">
                <FileText size={12} className="text-slate-400" />
                Patient Notes
              </label>
              <button
                data-testid="save-notes-btn"
                onClick={saveNotes}
                disabled={notesSaving}
                className={`h-7 px-3 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
                  notesSaved
                    ? "bg-emerald-100 text-emerald-700 border border-emerald-300"
                    : "bg-slate-800 hover:bg-slate-700 text-white"
                } disabled:opacity-50`}
              >
                {notesSaving ? (
                  <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : notesSaved ? (
                  "Saved!"
                ) : (
                  "Save Notes"
                )}
              </button>
            </div>
            <textarea
              data-testid="patient-notes-input"
              value={notes}
              onChange={e => { setNotes(e.target.value); setNotesSaved(false); }}
              placeholder="Add call notes, patient preferences, follow-up reminders..."
              rows={3}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none bg-slate-50"
            />
            {notesTimestamp.at && (
              <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                <Clock size={10} />
                Last updated {formatRelativeTime(notesTimestamp.at)}
                {notesTimestamp.by && <span>by <span className="font-medium text-slate-500">{notesTimestamp.by}</span></span>}
              </p>
            )}
          </div>

            </div>

            {/* ── REFERENCE SECTIONS ─────────────────────────────────
                Everything that used to sit between the phone number
                and View Documents. ALL DEFAULT COLLAPSED on every
                modal open (CollapsibleSection mounts closed and keeps
                no persisted state). Bodies stay MOUNTED while hidden,
                so unsaved in-progress edits survive a collapse. */}
            {(patient.email || canEditField("email")) && (
            <CollapsibleSection id="email" title="Patient Email" icon={Mail}>
            {/* Email */}
            {patient.email && (
              <div className="col-span-2">
                <a
                  href={`mailto:${patient.email}`}
                  data-testid="banner-email"
                  className="flex items-center gap-3 p-3 rounded-xl bg-blue-50 border border-blue-200 hover:bg-blue-100 transition-colors group"
                >
                  <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                    <Mail size={16} className="text-white" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-blue-700">Patient Email</p>
                    <p className="text-lg font-semibold text-blue-800">{patient.email}</p>
                  </div>
                </a>
              </div>
            )}

            {/* Item 4 (C2, 2026-09-21): email joins the front desk's
                correctable group, so it needs an editor — the mailto
                block above is a link, not a field. Rendered for every
                editing role; the section itself now appears when the
                email is EMPTY too, so a missing address can be added. */}
            <div className="col-span-2 mt-2" data-testid="email-edit-group">
              <EditableField
                icon={Mail}
                label="Patient Email"
                value={patientData.email}
                fieldName="email"
                patientId={patient.id}
                onSave={handleSaveField}
                canEdit={canEditField("email")}
                emptyLabel="No email on file"
              />
            </div>

            </CollapsibleSection>
            )}
            <CollapsibleSection id="physician" title="Referring Physician" icon={Stethoscope}>
            {/* Primary Physician / Referring Doctor — inline-editable group.
                Always rendered (even when name is empty) so staff can fill in
                a missing physician without needing to switch screens. */}
            <div
              className="col-span-2 p-3 rounded-xl bg-teal-50 border border-teal-200"
              data-testid="physician-group"
            >
              <div className="flex items-center gap-2 mb-2">
                <Stethoscope size={14} className="text-teal-600" />
                <span className="text-xs font-semibold text-teal-600 uppercase tracking-wide">Referring Physician</span>
                {/* 2026-06: amber "Needs NPI review" flag — set by n8n
                    Step 3 when the linked Raintree referral's NPI looks
                    suspicious. Reuses the ICD-10 review-badge styling
                    so the visual language is consistent. No tooltip:
                    the underlying reason is a dev string and is not
                    surfaced. */}
                {patientData.needs_npi_review === true && (
                  <span
                    data-testid="npi-review-badge"
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold text-xs bg-amber-100 text-amber-700"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                    Needs NPI review
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                <EditableField label="Physician Name"        value={patientData.primary_physician_name}  fieldName="primary_physician_name"  patientId={patient.id} onSave={handleSaveField} canEdit={canEditField("primary_physician_name")} />
                <EditableField label="Physician Organization" value={patientData.primary_physician_org}   fieldName="primary_physician_org"   patientId={patient.id} onSave={handleSaveField} canEdit={canEditField("primary_physician_org")} />
                <EditableField label="Physician Phone"        value={patientData.primary_physician_phone} fieldName="primary_physician_phone" patientId={patient.id} onSave={handleSaveField} canEdit={canEditField("primary_physician_phone")} />
                {/* 2026-06: Referral NPI = source of truth from
                    weststar_documents (n8n Step 3). Read-only — the
                    parsed `primary_physician_npi` is no longer
                    rendered. JSX shape matches EditableField's
                    read-only branch so the row visually slots into
                    the same grid. */}
                <div className="flex items-start gap-2 min-w-0" data-testid="readonly-linked_referral_npi">
                  <div className="min-w-0">
                    <p className="text-xs text-slate-400">Referral NPI (Raintree)</p>
                    <p className="text-sm font-medium text-slate-800 truncate">{patientData.linked_referral_npi || "—"}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2 min-w-0" data-testid="readonly-raintree_referral_id">
                  <div className="min-w-0">
                    <p className="text-xs text-slate-400">Referral ID</p>
                    <p className="text-sm font-medium text-slate-800 truncate">{patientData.raintree_referral_id || "—"}</p>
                  </div>
                </div>
              </div>
            </div>

            </CollapsibleSection>
            <CollapsibleSection id="address" title="Patient Address" icon={MapPin}>
            {/* Patient Address — inline-editable group. Always rendered.
                Four separate fields so each can write back to its own BQ
                column via the portal-edits queue. */}
            <div
              className="col-span-2 p-3 rounded-xl bg-slate-50 border border-slate-200"
              data-testid="address-group"
            >
              <div className="flex items-center gap-2 mb-2">
                <MapPin size={14} className="text-slate-500" />
                <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Patient Address</span>
              </div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                <div className="col-span-2">
                  <EditableField label="Address" value={patientData.patient_address} fieldName="patient_address" patientId={patient.id} onSave={handleSaveField} canEdit={canEditField("patient_address")} />
                </div>
                <EditableField label="City"  value={patientData.patient_city}  fieldName="patient_city"  patientId={patient.id} onSave={handleSaveField} canEdit={canEditField("patient_city")} />
                <EditableField label="State" value={patientData.patient_state} fieldName="patient_state" patientId={patient.id} onSave={handleSaveField} canEdit={canEditField("patient_state")} />
                <EditableField label="Zip"   value={patientData.patient_zip_code} fieldName="patient_zip_code" patientId={patient.id} onSave={handleSaveField} canEdit={canEditField("patient_zip_code")} />
              </div>
              {/* Item 4 (C2, 2026-09-21) — the routing hint. Correcting an
                  address does NOT move the patient to a nearer clinic and
                  does not re-route anything: assignment is its own
                  decision, made with the clinic field. Said plainly here
                  so a front-desk correction isn't mistaken for a
                  transfer. */}
              {canEditField("patient_address") && (
                <p
                  data-testid="address-routing-hint"
                  className="mt-2 text-xs text-slate-500 leading-snug"
                >
                  Correcting the address does not move the patient to a different
                  clinic. Use the clinic field to transfer them.
                </p>
              )}
            </div>

            </CollapsibleSection>
            <CollapsibleSection id="case" title={"Case & Insurance Details"} icon={Briefcase}>
              <div className="grid grid-cols-2 gap-x-8 gap-y-4">
            {/* For PI cases, show attorney prominently. Case Type is a
                constrained PI/WC dropdown (it buckets reports/dashboards);
                Claim # and Patient # are normal text edits. */}
            {patientData.case_type === "PI" ? (
              <>
                {patient.attorney_name && (
                  <div className="col-span-2 p-3 rounded-xl bg-purple-50 border border-purple-200">
                    <div className="flex items-center gap-2 mb-1">
                      <Briefcase size={14} className="text-purple-600" />
                      <span className="text-xs font-semibold text-purple-600 uppercase tracking-wide">Attorney (PI Case)</span>
                    </div>
                    <p className="text-sm font-bold text-purple-900">{patient.attorney_name}</p>
                    {patient.attorney_phone && (
                      <a href={`tel:${patient.attorney_phone}`} className="text-xs text-purple-700 hover:text-purple-900 font-mono hover:underline">
                        {patient.attorney_phone}
                      </a>
                    )}
                  </div>
                )}
                <EditableField icon={Briefcase} label="Case Type" value={patientData.case_type} fieldName="case_type" patientId={patient.id} onSave={handleSaveField} canEdit={canEditField("case_type")} options={["PI", "WC"]} />
              </>
            ) : (
              <>
                <EditableField icon={FileText} label="Claim #" value={patientData.claim_number} fieldName="claim_number" patientId={patient.id} onSave={handleSaveField} canEdit={canEditField("claim_number")} />
                <EditableField icon={Briefcase} label="Case Type" value={patientData.case_type} fieldName="case_type" patientId={patient.id} onSave={handleSaveField} canEdit={canEditField("case_type")} options={["PI", "WC"]} />
              </>
            )}
            {/* Intake Type — constrained clinical classification, same
                dropdown treatment as Case Type. Null / out-of-set values
                use the disabled "Select…" placeholder in edit mode. */}
            <EditableField icon={Briefcase} label="Intake Type" value={patientData.intake_type} fieldName="intake_type" patientId={patient.id} onSave={handleSaveField} canEdit={canEditField("intake_type")} options={INTAKE_TYPE_OPTIONS} />
            {/* SSN — plumbed through the patient's BQ file_id to the intake
                SSN endpoints; never written to or read from Postgres. */}
            {/* SSN is OUT of the front desk's group (item 4, C2) — the
                location role loses this editor; sales/admin keep it. */}
            <CrmSsnField fileId={patientData.bq_file_id} canEdit={canEditField("patient_ssn")} />
            <EditableField icon={Building2} label="Employer" value={patientData.employer_name} fieldName="employer_name" patientId={patient.id} onSave={handleSaveField} canEdit={canEditField("employer_name")} />
            {/* Assigned Clinic — read-only DetailRow (2026-02). The
                editable path moved to the location dropdown above Status
                (single-source-of-truth). The paired writer at
                PUT /crm/patients/{id}/assigned-clinic updates both
                assigned_clinic + assigned_clinic_city atomically and
                stamps locally_modified so the CRM sync can't revert. */}
            <DetailRow
              icon={Building2}
              label="Assigned Clinic"
              value={patientData.assigned_clinic
                ? (patientData.assigned_clinic_city
                    ? `${patientData.assigned_clinic} — ${patientData.assigned_clinic_city}`
                    : patientData.assigned_clinic)
                : null}
            />
            <EditableField icon={User}      label="Therapy Type"   value={patientData.therapy_type} fieldName="therapy_type" patientId={patient.id} onSave={handleSaveField} canEdit={canEditField("therapy_type")} />
            {/* Item 7 (2026-09-20) — ONE injury-date value:
                COALESCE(date_of_injury, date_of_accident). PI referrals
                carry the accident date where WC carry the injury date, so
                a PI card used to read "N/A" with the date sitting right
                there in BigQuery. The label follows which value survived.
                The edit target stays `date_of_injury` — no portal path
                writes DOA (Intake owns it). */}
            <EditableField
              icon={CalendarDays}
              label={injuryDateLabel(patientData)}
              value={injuryDateValue(patientData)}
              fieldName="date_of_injury"
              patientId={patient.id}
              onSave={handleSaveField}
              canEdit={canEditField("date_of_injury")}
              type="date"
              emptyLabel="missing"
              emptyWarning
            />
            <EditableField icon={FileText}  label="Payor"          value={patientData.insurance_carrier} fieldName="insurance_carrier" patientId={patient.id} onSave={handleSaveField} canEdit={canEditField("insurance_carrier")} />
            {/* Item 1 (2026-09-20): the Raintree patient number moved to
                the card HEADER (`banner-patient-pn`) and the editable copy
                that used to sit here was removed, so the PN appears exactly
                once. Corrections happen on the Referral Intake tab, which
                writes BigQuery — this path wrote Postgres only and left the
                two stores disagreeing. */}            <DetailRow icon={Clock}     label="Date Added"     value={patientData.date_added ? new Date(patientData.date_added).toLocaleString() : null} />
            {patientData.adjuster_name && <EditableField icon={User} label="Adjuster" value={patientData.adjuster_name} fieldName="adjuster_name" patientId={patient.id} onSave={handleSaveField} canEdit={canEditField("adjuster_name")} />}
            {patientData.case_type !== "PI" && patientData.attorney_name && <EditableField icon={User} label="Attorney" value={patientData.attorney_name} fieldName="attorney_name" patientId={patient.id} onSave={handleSaveField} canEdit={canEditField("attorney_name")} />}
            {patientData.body_parts_involved && (
              <div className="col-span-2">
                <EditableField icon={User} label="Body Parts" value={patientData.body_parts_involved} fieldName="body_parts_involved" patientId={patient.id} onSave={handleSaveField} canEdit={canEditField("body_parts_involved")} />
              </div>
            )}

            {/* Authorized Treatments — read-only, parsed from the JSON array
                stored in BQ weststar_documents.authorized_treatments. Shows
                an em dash when empty so the panel stays consistent. */}
            <div className="col-span-2">
              <AuthorizedTreatmentsField value={patientData.authorized_treatments} />
            </div>

              </div>
            </CollapsibleSection>
            <CollapsibleSection id="icd10" title="ICD-10 Codes" icon={FileText}>
            {/* ICD-10 review/edit panel (2026-06-11).
                Same component used by Referral Intake — single source of
                truth in `src/components/Icd10Panel.jsx`. The CRM Queue's
                "Please Review" flag is driven by `icd10_review` from the
                backend; before this panel landed here, queue staff had no
                way to actually act on that flag. Save → PUT
                /api/intake/referrals/{file_id}/icd10 which stamps
                icd10_confirmed_at + marks the row locally-modified so the
                regular CRM sync won't revert the change. Edit gate
                (`canEdit = admin || sales`) lives inside the component —
                we deliberately do NOT pass `canEditFields` here because
                that gate is intake-only and irrelevant in the queue. */}
            <div className="col-span-2">
              <Icd10Panel
                codes={patientData.icd10_codes}
                review={patientData.icd10_review}
                fileId={patientData.bq_file_id}
                userRole={user?.role}
                onUpdate={(savedCodes) => {
                  // Optimistically clear the review flag + apply the
                  // saved codes locally so the badge disappears and the
                  // list updates without a full page reload. The next
                  // patient-list refresh will reconcile against the BQ
                  // BackgroundTask UPDATE.
                  setPatientData((prev) => ({
                    ...prev,
                    icd10_codes: savedCodes,
                    icd10_review: false,
                  }));
                  if (onPatientUpdated) {
                    onPatientUpdated(patient.id, {
                      icd10_codes: savedCodes,
                      icd10_review: false,
                    });
                  }
                }}
              />
            </div>

            </CollapsibleSection>

            {/* View Documents — last content block in the modal. */}
            {/* View Documents — same SharePoint folder the Referral Intake page links to.
                Constructed server-side in Pacific tz; null when we can't derive a URL. */}
            <div className="col-span-2">
              {patientData.onedrive_folder_url ? (
                <a
                  href={patientData.onedrive_folder_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                  data-testid="crm-view-documents-btn"
                >
                  <FolderOpen size={16} />
                  View Documents
                  <ExternalLink size={13} className="ml-1 opacity-70" />
                </a>
              ) : (
                <div
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg flex items-start gap-3"
                  data-testid="crm-view-documents-empty"
                >
                  <FolderOpen size={16} className="text-slate-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-slate-700">Documents not yet attached</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Medical records, lien forms and billing docs appear here once they're uploaded to the SharePoint referral folder.
                    </p>
                  </div>
                </div>
              )}
            </div>

          </div>

          {/* Next Patient button - always visible at bottom.
              Ticket 1 (2026-09-05): in queue mode the rep is locked into the
              served patient until a qualifying action is recorded — a
              status/stage change, a callback, or a booking. There is no skip
              affordance any more (the endpoint and its history stay; only the
              button is gone), so Next is the single exit and it needs an
              action first. Direct view (opened from the list) is unaffected. */}
          <div className="px-6 pb-4 flex flex-col justify-center items-center gap-2">
            <button
              data-testid="next-patient-btn"
              onClick={onNext}
              disabled={nextLocked || writing}
              title={nextLocked ? NEXT_LOCKED_COPY : undefined}
              className={`px-6 py-2.5 font-semibold text-sm rounded-xl transition-colors shadow-sm flex items-center gap-2 ${
                nextLocked
                  ? "bg-slate-200 text-slate-500 cursor-not-allowed shadow-none"
                  : "bg-emerald-600 hover:bg-emerald-700 text-white"
              }`}
            >
              <ChevronRight size={16} />
              {writing ? "Saving…" : "Next Patient in Queue"}
            </button>
            {nextLocked && (
              <p
                data-testid="next-patient-locked-note"
                className="text-xs text-slate-500 flex items-center gap-1.5"
              >
                <Lock size={12} className="text-slate-400" />
                {NEXT_LOCKED_COPY}
              </p>
            )}
          </div>
        </div>
        <style>{`@keyframes slideUp { from { opacity:0; transform:translateY(24px); } to { opacity:1; transform:translateY(0); } }`}</style>
      </div>

      {showSchedule && (
        <ScheduleModal
          patient={patient}
          location={selectedLocation}
          dateStart={dateStart}
          dateEnd={dateEnd}
          rescheduleApptId={rescheduleApptId}
          rescheduleFrom={rescheduleFrom}
          onClose={() => { setShowSchedule(false); setRescheduleApptId(null); }}
          onBooked={handleBooked}
        />
      )}

      {/* Callback Modal */}
      {showCallbackModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShowCallbackModal(false)}>
          <div 
            data-testid="callback-modal"
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 mx-4"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <Clock size={20} className="text-amber-600" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-slate-900">Schedule Callback</h3>
                <p className="text-sm text-slate-500">{patient.full_name}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Date</label>
                <input
                  type="date"
                  value={callbackDate}
                  min={today}
                  onChange={e => setCallbackDate(e.target.value)}
                  className="w-full h-10 px-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Time</label>
                <input
                  type="time"
                  value={callbackTime}
                  onChange={e => setCallbackTime(e.target.value)}
                  className="w-full h-10 px-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Reason (optional)</label>
                <input
                  type="text"
                  value={callbackReason}
                  onChange={e => setCallbackReason(e.target.value)}
                  placeholder="e.g., Patient requested afternoon call"
                  className="w-full h-10 px-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setShowCallbackModal(false)}
                className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                data-testid="callback-save-btn"
                onClick={saveCallback}
                disabled={!callbackDate || !callbackTime || callbackSaving}
                className="px-5 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold disabled:opacity-40 transition-colors flex items-center gap-2"
              >
                {callbackSaving ? (
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Clock size={14} />
                )}
                Set Callback
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assigned-clinic pair confirmation dialog (2026-02). Rendered only
          when the user's dropdown change triggers Cond A (WestStar↔Network
          flip) or Cond B (patient has a future appointment at the current
          location). Same-classification changes with no future appointment
          apply silently and never reach here. */}
      {assignedClinicConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
          data-testid="assigned-clinic-confirm-modal"
          onClick={() => setAssignedClinicConfirm(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-slate-800 mb-3">Change assigned clinic?</h3>
            <div className="space-y-2 text-sm text-slate-700">
              <p data-testid="assigned-clinic-confirm-summary">
                Assigned clinic will change from{" "}
                <span className="font-semibold">
                  {patientData.assigned_clinic || "(none)"}
                  {patientData.assigned_clinic_city ? ` — ${patientData.assigned_clinic_city}` : ""}
                </span>
                {" "}to{" "}
                <span className="font-semibold">
                  {assignedClinicConfirm.nextLoc?.clinic_name || "(none)"}
                  {assignedClinicConfirm.nextLoc?.city ? ` — ${assignedClinicConfirm.nextLoc.city}` : ""}
                </span>
                .
              </p>
              {assignedClinicConfirm.reasons.map((r, i) => {
                if (r.kind === "classification_flip") {
                  return (
                    <p key={i} data-testid="assigned-clinic-confirm-classflip" className="text-amber-700">
                      This moves the patient from <span className="font-semibold">{r.from === "weststar" ? "WestStar" : "Network"}</span> to <span className="font-semibold">{r.to === "weststar" ? "WestStar" : "Network"}</span>.
                    </p>
                  );
                }
                if (r.kind === "future_appt_at_current") {
                  return (
                    <div key={i} data-testid="assigned-clinic-confirm-futureappt" className="text-amber-700">
                      {r.appointments.map((a, j) => (
                        <p key={j}>
                          This patient has an appointment on{" "}
                          <span className="font-semibold">
                            {formatWallClockDateTime(a.when)}
                          </span>{" "}at <span className="font-semibold">{a.location_name}</span>.
                        </p>
                      ))}
                    </div>
                  );
                }
                return null;
              })}
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={() => setAssignedClinicConfirm(null)}
                className="px-4 py-2 rounded-xl border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50 transition-colors"
                data-testid="assigned-clinic-confirm-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="assigned-clinic-confirm-accept"
                disabled={assignedClinicSaving}
                onClick={async () => {
                  const nextLoc = assignedClinicConfirm.nextLoc;
                  setAssignedClinicConfirm(null);
                  await persistAssignedClinicPair(nextLoc);
                }}
                className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-50 transition-colors"
              >
                {assignedClinicSaving ? "Saving…" : "Change clinic"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Item 4 (C2, 2026-09-21) — DOB confirmation. A wrong DOB breaks
          the match staff use to find a patient, so the change is stated
          old → new and confirmed before it is sent. */}
      {dobConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
          data-testid="dob-confirm-modal"
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-slate-800 mb-3">Change date of birth?</h3>
            <p className="text-sm text-slate-700" data-testid="dob-confirm-summary">
              Date of birth will change from{" "}
              <span className="font-semibold">{formatDOB(patientData.date_of_birth) || "(none)"}</span>{" "}
              to{" "}
              <span className="font-semibold">{formatDOB(dobConfirm.newValue) || "(none)"}</span>.
            </p>
            <p className="mt-2 text-xs text-amber-700">
              Staff match patients by name and date of birth — a wrong date makes
              this patient hard to find.
            </p>
            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                data-testid="dob-confirm-cancel"
                onClick={() => {
                  const c = dobConfirm;
                  setDobConfirm(null);
                  c.reject(new Error("dob_change_cancelled"));
                }}
                className="px-4 py-2 rounded-xl border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="dob-confirm-accept"
                onClick={async () => {
                  const c = dobConfirm;
                  setDobConfirm(null);
                  try {
                    await handleSaveField(c.fieldName, c.newValue);
                    c.resolve();
                  } catch (err) {
                    c.reject(err);
                  }
                }}
                className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors"
              >
                Change date of birth
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Item 5 (C2, 2026-09-21) — the scope consequence, stated. The
          transfer succeeded, the fence moved with the pair, and this card
          is no longer this clinic's to work. Acknowledging closes the
          panel (the row is already gone from their queue). */}
      {transferredAway && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
          data-testid="fd-transferred-away-modal"
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-slate-800 mb-3">Patient transferred</h3>
            <p className="text-sm text-slate-700" data-testid="fd-transferred-away-summary">
              {patient.first_name} {patient.last_name} is now assigned to{" "}
              <span className="font-semibold">
                {transferredAway.clinic}
                {transferredAway.city ? ` — ${transferredAway.city}` : ""}
              </span>
              . That clinic works the patient from here, so this card has left
              your queue.
            </p>
            <div className="flex justify-end mt-6">
              <button
                type="button"
                data-testid="fd-transferred-away-ack"
                onClick={() => { setTransferredAway(null); if (onRelease) onRelease(); }}
                className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Authorized Treatments (read-only) ────────────────────────────────────────
// Renders the JSON-string `authorized_treatments` from BQ as a tidy list.
// Each item gets one row: treatment name + a frequency tag derived from
// visits_per_week / duration_weeks / total_visits / frequency_raw (whichever
// is most readable). Body part is appended as a slate badge when present.
// Falls back to "—" for empty / unparseable / null values so the panel stays
// visually consistent.
function AuthorizedTreatmentsField({ value }) {
  let items = null;
  if (value) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) items = parsed;
    } catch {
      // Not JSON — display the raw string as a single-item list.
      items = [{ treatment: value }];
    }
  }

  return (
    <div data-testid="authorized-treatments" className="flex items-start gap-2 min-w-0">
      <Stethoscope size={13} className="text-slate-400 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-slate-400 mb-1">Authorized Treatments</p>
        {!items || items.length === 0 ? (
          <p className="text-sm text-slate-400" data-testid="authorized-treatments-empty">—</p>
        ) : (
          <ul className="space-y-1.5">
            {items.map((it, idx) => {
              const freq =
                it.frequency_raw ||
                (it.visits_per_week && it.duration_weeks
                  ? `${it.visits_per_week}×/wk · ${it.duration_weeks} wk${it.duration_weeks > 1 ? "s" : ""}`
                  : it.total_visits
                  ? `${it.total_visits} visit${it.total_visits > 1 ? "s" : ""}`
                  : null);
              return (
                <li key={idx} className="text-sm text-slate-700 flex items-start gap-1.5 flex-wrap" data-testid={`authorized-treatment-${idx}`}>
                  <span className="font-medium">{it.treatment || "—"}</span>
                  {it.body_part && (
                    <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{it.body_part}</span>
                  )}
                  {freq && (
                    <span className="text-xs text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded">{freq}</span>
                  )}
                  {it.source && (
                    <span className="text-[10px] text-slate-400 italic">· {it.source}</span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}


// P-5 (2026-10-02) — SMS consent on the patient panel, under Patient Phone.
// Staff-assisted (Consent v0.3 §3): the patient agrees on the call, staff
// ticks the box and presses Record. The server owns every rule (never over a
// STOP, one door, untextable numbers refused); this only reflects them.
function SmsConsentPanel({ patient, readOnly = false, onRecorded }) {
  const [ticked, setTicked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  if (patient.sms_consent === false) {
    return (
      <div
        data-testid="sms-consent-panel-stop"
        className="mt-2 text-xs rounded-lg border border-rose-200 bg-rose-50 text-rose-800 px-3 py-2"
      >
        This patient replied STOP, so texts are off. Staff can't turn them back on. The patient restarts them by replying START.
      </div>
    );
  }

  if (patient.sms_consent) {
    const when = patient.sms_consent_at ? new Date(patient.sms_consent_at).toLocaleDateString() : null;
    return (
      <div
        data-testid="sms-consent-panel-recorded"
        className="mt-2 text-xs rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-800 px-3 py-2"
      >
        Text consent on record
        {patient.sms_consent_source ? ` (${patient.sms_consent_source}${patient.sms_consent_version ? `, ${patient.sms_consent_version}` : ""})` : ""}
        {when ? `, ${when}` : ""}.
      </div>
    );
  }

  const record = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await api.put(`/crm/patients/${patient.id}/sms-consent`, { consent: true });
      const d = res.data || {};
      if (onRecorded) {
        onRecorded({
          sms_consent: d.sms_consent,
          sms_consent_source: d.sms_consent_source,
          sms_consent_version: d.sms_consent_version,
          sms_consent_at: d.sms_consent_at,
        });
      }
    } catch (err) {
      const status = err?.response?.status;
      const code = err?.response?.data?.detail;
      if (status === 409 && code === "stop_on_record") {
        if (onRecorded) onRecorded({ sms_consent: false });
      } else if (code === "phone_not_textable") {
        setError("This number can't receive texts. Fix the phone number first.");
      } else {
        setError("Couldn't record consent. Try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="sms-consent-panel" className="mt-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
      <label className="flex items-start gap-2 text-xs text-slate-700">
        <input
          type="checkbox"
          checked={ticked}
          disabled={readOnly || saving}
          onChange={(e) => setTicked(e.target.checked)}
          className="mt-0.5"
          data-testid="sms-consent-panel-checkbox"
        />
        <span data-testid="sms-consent-panel-label">{SMS_CONSENT_CHECKBOX_TEXT}</span>
      </label>
      <details className="mt-1 ml-5">
        <summary className="text-[11px] text-teal-700 cursor-pointer" data-testid="sms-consent-panel-details-toggle">
          Full text message consent language (v0.3)
        </summary>
        <p className="mt-1 text-[11px] text-slate-500 leading-relaxed" data-testid="sms-consent-panel-full-text">
          {SMS_CONSENT_FULL_TEXT}
        </p>
      </details>
      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          onClick={record}
          disabled={!ticked || readOnly || saving}
          data-testid="sms-consent-record-btn"
          className="px-2.5 py-1 rounded-md text-xs font-semibold bg-emerald-600 text-white disabled:bg-slate-200 disabled:text-slate-500"
        >
          {saving ? "Recording…" : "Record consent"}
        </button>
        {error && (
          <span className="text-[11px] text-rose-700" data-testid="sms-consent-panel-error">{error}</span>
        )}
      </div>
    </div>
  );
}

function DetailRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 min-w-0">
      <Icon size={13} className="text-slate-400 mt-0.5 flex-shrink-0" />
      <div className="min-w-0">
        <p className="text-xs text-slate-400">{label}</p>
        <p className="text-sm font-medium text-slate-800 truncate">{value}</p>
      </div>
    </div>
  );
}

// ── Editable Field Component ───────────────────────────────────────────────────
// ── SSN field (CRM modal) ─────────────────────────────────────────────────────
// Same three-state behavior as the Referral Intake SsnField, but self-managed:
// SSN never enters Postgres, so the CRM modal plumbs the patient's BQ file_id
// to the intake endpoints — GET /intake/referrals/{fid}/ssn-status on modal
// open, the audited GET .../ssn for Reveal (30s auto-remask), and the intake
// field-edit PUT for corrections. Missing/broken file_id or a 404 degrades
// silently to a non-editable "SSN unavailable" (no fetch spam, no toasts).
function CrmSsnField({ fileId, canEdit }) {
  const { user } = useAuth();
  const [status, setStatus] = useState(null); // {state,last4} | "unavailable"
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [revealed, setRevealed] = useState(null);
  const [revealing, setRevealing] = useState(false);
  const inputRef = useRef(null);
  const timerRef = useRef(null);

  const hide = useCallback(() => {
    setRevealed(null);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    // The intake endpoints deny the WS Location role (2026-09) — don't
    // call them just to swallow a 403 twice per modal open.
    if (!fileId || user?.role === "location") {
      setStatus("unavailable");
      return;
    }
    try {
      const res = await api.get(`/intake/referrals/${fileId}/ssn-status`);
      setStatus(res.data);
    } catch {
      setStatus("unavailable"); // 404 / broken file_id — degrade silently
    }
  }, [fileId, user?.role]);

  useEffect(() => {
    hide();
    setIsEditing(false);
    setEditValue("");
    setStatus(null);
    fetchStatus();
    return hide;
  }, [fileId, fetchStatus, hide]);

  useEffect(() => {
    if (isEditing && inputRef.current) inputRef.current.focus();
  }, [isEditing]);

  const state = status && status !== "unavailable" ? status.state : null;
  const last4 = status && status !== "unavailable" ? status.last4 : null;

  const handleReveal = async () => {
    if (revealed) {
      hide();
      return;
    }
    setRevealing(true);
    try {
      const res = await api.get(`/intake/referrals/${fileId}/ssn`);
      setRevealed(res.data.ssn);
      timerRef.current = setTimeout(hide, 30000); // auto-remask after 30s
    } catch (err) {
      console.error("SSN reveal failed"); // never log the value
      alert(err?.response?.data?.detail || "Failed to reveal SSN");
    } finally {
      setRevealing(false);
    }
  };

  const handleCancel = () => {
    setEditValue("");
    setIsEditing(false);
  };

  const handleSave = async () => {
    const trimmed = editValue.trim();
    if (trimmed === "" && state !== "on_file") {
      // No-op: preserves the sentinel ('unknown') and NULL ('none') alike.
      handleCancel();
      return;
    }
    setSaving(true);
    try {
      await api.put(`/intake/referrals/${fileId}`, {
        fields: { patient_ssn: trimmed === "" ? null : trimmed },
      });
      setIsEditing(false);
      setEditValue(""); // never keep the full value in state after save
      hide();
      fetchStatus();
    } catch (err) {
      alert(err?.response?.data?.detail || "Failed to save SSN");
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSave();
    if (e.key === "Escape") handleCancel();
  };

  const masked = state === "on_file" && last4 ? `•••-••-${last4}` : null;

  return (
    <div className="flex items-start gap-2 min-w-0 group" data-testid="crm-ssn-field">
      <FileText size={13} className="text-slate-400 mt-0.5 flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-xs text-slate-400">Social Security Number</p>
        {status === "unavailable" ? (
          <p className="text-sm font-medium text-slate-400" data-testid="crm-ssn-display">SSN unavailable</p>
        ) : status === null ? (
          <p className="text-sm font-medium text-slate-400" data-testid="crm-ssn-display">…</p>
        ) : isEditing ? (
          <div className="flex items-center gap-1 mt-0.5">
            <input
              ref={inputRef}
              type="text"
              inputMode="numeric"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value.replace(/[^\d-]/g, ""))}
              onKeyDown={handleKeyDown}
              placeholder="###-##-#### (9 digits)"
              maxLength={11}
              disabled={saving}
              className="flex-1 text-sm px-2 py-1 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-0"
              data-testid="crm-ssn-input"
            />
            {saving ? (
              <Loader2 size={14} className="animate-spin text-slate-400" />
            ) : (
              <>
                <button onClick={handleSave} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded" title="Save" data-testid="crm-ssn-save">
                  <Check size={14} />
                </button>
                <button onClick={handleCancel} className="p-1 text-red-500 hover:bg-red-50 rounded" title="Cancel" data-testid="crm-ssn-cancel">
                  <XCircle size={14} />
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-1">
            {state === "unknown" ? (
              <p className="text-sm font-medium text-slate-500" data-testid="crm-ssn-display">
                999-99-9999 <span className="text-xs font-normal text-slate-400">(not provided)</span>
              </p>
            ) : (
              <p className="text-sm font-medium text-slate-800" data-testid="crm-ssn-display">
                {revealed || masked || "N/A"}
              </p>
            )}
            {masked && (
              <button
                onClick={handleReveal}
                disabled={revealing}
                className="p-0.5 text-slate-400 hover:text-blue-600"
                title={revealed ? "Hide SSN" : "Reveal SSN (audited)"}
                data-testid="crm-ssn-reveal-btn"
              >
                {revealing ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : revealed ? (
                  <EyeOff size={12} />
                ) : (
                  <Eye size={12} />
                )}
              </button>
            )}
            {canEdit && (
              <button
                onClick={() => {
                  hide();
                  setIsEditing(true);
                }}
                className="p-0.5 text-slate-400 hover:text-blue-600 opacity-70 hover:opacity-100 transition-opacity"
                title="Edit Social Security Number"
                data-testid="crm-ssn-edit-btn"
              >
                <Edit2 size={12} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function EditableField({ icon: Icon, label, value, fieldName, patientId, onSave, canEdit = false, type = "text", suffix = null, options = null, required = false, emptyLabel = "N/A", emptyWarning = false }) {
  const [isEditing, setIsEditing] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [editValue, setEditValue] = useState(value || "");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  useEffect(() => {
    setEditValue(value || "");
  }, [value]);

  const handleSave = async () => {
    // Validation failures stay INLINE next to the offending field (see the
    // `-error` node below). Network / server save failures are a different
    // class and keep the pre-existing behaviour (console + the caller's
    // toast) — deliberately not rerouted here.
    if (required && !String(editValue || "").trim()) {
      setValidationError(`${label} can't be empty`);
      return;   // stays in edit mode, keeps what the user typed
    }
    setValidationError(null);
    if (editValue === (value || "")) {
      setIsEditing(false);
      return;
    }
    // 2026-08-20 hotfix: for date-only fields, force the outgoing
    // payload to a bare YYYY-MM-DD string. `toDateOnlyString` never
    // calls `.toISOString()`, so an evening edit can't drift +1 via
    // UTC conversion. Non-date fields pass through untouched.
    const outgoing = type === "date" ? toDateOnlyString(editValue) : editValue;
    setSaving(true);
    try {
      await onSave(fieldName, outgoing);
      setIsEditing(false);
    } catch (err) {
      console.error("Failed to save:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditValue(value || "");
    setValidationError(null);
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  const displayValue = type === "date" && value ? formatDOI(value) : (value || emptyLabel);
  // Item 7 (2026-09-20): a field can ask for its own empty label and for
  // that label to READ as a gap (the injury date renders "missing" in the
  // warning style so it gets corrected on the Intake tab, never "N/A").
  const emptyStyle = !value && emptyWarning ? "text-amber-700 font-semibold" : "text-slate-800";

  if (!canEdit) {
    return (
      <div className="flex items-start gap-2 min-w-0" data-testid={`editable-${fieldName}`}>
        {Icon && <Icon size={13} className="text-slate-400 mt-0.5 flex-shrink-0" />}
        <div className="min-w-0">
          <p className="text-xs text-slate-400">{label}</p>
          <p className={`text-sm font-medium truncate ${emptyStyle}`} data-testid={`editable-${fieldName}-value`}>
            {displayValue}
            {suffix && <span className="font-normal text-slate-500" data-testid={`editable-${fieldName}-suffix`}> – {suffix}</span>}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-2 min-w-0 group" data-testid={`editable-${fieldName}`}>
      {Icon && <Icon size={13} className="text-slate-400 mt-0.5 flex-shrink-0" />}
      <div className="min-w-0 flex-1">
        <p className="text-xs text-slate-400">{label}</p>
        {isEditing ? (
          <>
          <div className="flex items-center gap-1 mt-0.5">
            {options ? (
              <select
                ref={inputRef}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={handleKeyDown}
                className="flex-1 text-sm px-2 py-1 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-0 bg-white"
                disabled={saving}
                data-testid={`editable-${fieldName}-select`}
              >
                {!options.includes(editValue) && <option value="" disabled>Select…</option>}
                {options.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            ) : (
              <input
                ref={inputRef}
                type={type === "date" ? "date" : "text"}
                value={type === "date" && editValue ? editValue.split("T")[0] : editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={handleKeyDown}
                className="flex-1 text-sm px-2 py-1 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-0"
                disabled={saving}
              />
            )}
            <button
              onClick={handleSave}
              disabled={saving}
              className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"
              title="Save"
            >
              <Check size={14} />
            </button>
            <button
              onClick={handleCancel}
              disabled={saving}
              className="p-1 text-red-500 hover:bg-red-50 rounded"
              title="Cancel"
            >
              <XCircle size={14} />
            </button>
          </div>
          {validationError && (
            <p className="mt-1 text-xs text-red-600" data-testid={`editable-${fieldName}-error`}>
              {validationError}
            </p>
          )}
          </>
        ) : (
          <div className="flex items-center gap-1">
            <p className={`text-sm font-medium truncate ${emptyStyle}`} data-testid={`editable-${fieldName}-value`}>
              {displayValue}
              {suffix && <span className="font-normal text-slate-500" data-testid={`editable-${fieldName}-suffix`}> – {suffix}</span>}
            </p>
            <button
              onClick={() => setIsEditing(true)}
              data-testid={`editable-${fieldName}-pencil`}
              className="p-0.5 text-slate-400 hover:text-blue-600 opacity-70 hover:opacity-100 transition-opacity"
              title={`Edit ${label}`}
            >
              <Edit2 size={12} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Confirmation toast ────────────────────────────────────────────────────────
function Toast({ message, type }) {
  if (!message) return null;
  const styles = type === "success"
    ? "bg-emerald-600 text-white"
    : "bg-slate-800 text-white";
  return (
    <div className={`fixed bottom-6 right-6 z-50 px-5 py-3 rounded-xl shadow-lg text-sm font-medium ${styles}`}
      style={{ animation: "slideUp 0.2s ease-out" }}>
      {message}
    </div>
  );
}

// ── Main CRM Queue page ───────────────────────────────────────────────────────
export default function CRMQueue() {
  // CRM statuses v2: null unless STATUS_NAMES_V2 covers this login.
  const stageModel = useStagesV2();
  const [patients, setPatients] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(
    // Patient Search deep-link prefill: /crm-queue?q=… (server-side search)
    () => new URLSearchParams(window.location.search).get("q") || ""
  );
  const [stageFilter, setStageFilter] = useState("");
  // WestStar/Network master filter — view-state only (resets to Both on
  // revisit). Composed server-side into BOTH the list and stage-counts
  // endpoints; never a client-side slice of a paginated page.
  const [locationFilter, setLocationFilter] = useState("both");
  const [pickedOnly, setPickedOnly] = useState(false);   // PR #9
  const [telehealthOnly, setTelehealthOnly] = useState(false);   // telehealth list (2026-10-07)
  // Continuing view split — separate team's view. Filtering is SERVER-SIDE
  // (the `continuing` param on /crm/patients); this flag just drives it.
  const [continuingView, setContinuingView] = useState(false);
  // Item 9 (2026-09-20) — default OFF: the Continuing view shows open
  // cards plus stage 12 only, so a card the clinic closes actually leaves.
  const [showClosedContinuing, setShowClosedContinuing] = useState(false);

  // Whole-population aggregate from /crm/patients/stage-counts. Pills and
  // the Continuing button read from this — NEVER derived from the loaded
  // page of rows (which broke counts at >200 patients).
  const [serverCounts, setServerCounts] = useState({ regular: {}, continuing_total: 0 });
  // ONE sort for the whole table (2026-09): { by, dir } | null. Every
  // column rides the same server-side mechanism and only one sort can be
  // active — null is the queue's default priority order.
  const [sort, setSort] = useState(null);

  // Queue mode state
  const [queueMode, setQueueMode] = useState(false);
  const [currentPatient, setCurrentPatient] = useState(null);
  const currentIdRef = useRef(null); currentIdRef.current = currentPatient?.id ?? null; // 2026-10-24: late answers check their card
  const [acting, setActing] = useState(false);
  const [queueEmpty, setQueueEmpty] = useState(false);
  const [toast, setToast] = useState({ message: "", type: "success" });
  const [locations, setLocations] = useState([]);
  const [isDirectView, setIsDirectView] = useState(false); // clicked from table vs queue lock
  // The engine's own why-served string for the CURRENTLY SERVED patient,
  // straight off the serve payload. Never recomputed here; cleared whenever
  // the served patient is no longer the engine's choice (exit, action,
  // direct-click view).
  const [serveReason, setServeReason] = useState(null);
  const [serveReasonIsSnapshot, setServeReasonIsSnapshot] = useState(false);

  // Ticket 1 lock-in: the id of the patient a qualifying action has been
  // saved on in THIS session. Keyed by id rather than a boolean so a new
  // serve is locked again automatically — there is no reset to forget — and
  // so the unlock is read-your-own-write (set from the save's own response
  // path, no refetch, no re-serve). Qualifying = a status/stage change, a
  // callback, or a booking. A note-only save and a plain field edit do NOT
  // qualify (WestStar §11: notes cannot substitute for a disposition, and a
  // note sets no next_due_at, which would strand the patient).
  const [actionSavedForId, setActionSavedForId] = useState(null);


  const { user } = useAuth();
  const searchTimer = useRef(null);

  // Fetch PT clinic locations. NOTE: no role filtering here (2026-09).
  // A legacy filter used to narrow this list for the location role using
  // `users.location_id` — a config slug ('west-star-anaheim') compared
  // against this endpoint's own slugs, so it matched nothing and left the
  // role with an empty dropdown and a dead Schedule button. It also capped
  // a multi-location user at one scalar site. `users.location_id` is
  // LEGACY READ-ONLY (see services/location_scope.py); the role's real set
  // is `user_locations`, applied in QueueBanner.filteredLocations via
  // useMyLocationCodes(), and enforced server-side on every booking call.
  useEffect(() => {
    api.get("/crm/locations").then(r => {
      setLocations(r.data.locations || []);
    }).catch(() => {});
  }, [user]);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast({ message: "", type: "success" }), 3000);
  };

  const fetchPatients = useCallback(async () => {
    setLoading(true);
    try {
      // Server-side filtering: stage_id + continuing + sort + pagination all
      // compose in list_patients over the same set — a stage's patients
      // spread across pages are all reachable via the stage filter.
      const params = { page, per_page: 200, continuing: continuingView };
      // Item 9 (2026-09-20): the Continuing view hides closed cards
      // (stages 8-11) unless the user asks. Sent ONLY in that view, so the
      // regular view's request is byte-identical to before.
      if (continuingView && showClosedContinuing) params.show_closed = true;
      if (!continuingView && stageFilter) params.stage_id = stageFilter;
      if (search) params.search = search;
      if (locationFilter !== "both") params.location_filter = locationFilter;
      if (pickedOnly) params.patient_picked = true;   // PR #9: sent only when on
      if (telehealthOnly) params.telehealth_list = true;   // sent only when on
      if (sort) {
        params.sort_by = sort.by;
        params.sort_dir = sort.dir;
      }
      const countsParams = locationFilter !== "both" ? { location_filter: locationFilter } : {};
      const [res, countsRes] = await Promise.all([
        api.get("/crm/patients", { params }),
        api.get("/crm/patients/stage-counts", { params: countsParams }),
      ]);
      setPatients(res.data.patients || []);
      setTotal(res.data.total || 0);
      setServerCounts(countsRes.data || { regular: {}, continuing_total: 0 });
    } catch (err) {
      console.error("CRM fetch error", err);
    } finally {
      setLoading(false);
    }
    // The 5s live refresh re-runs THIS callback, so the chosen sort rides
    // every refresh (it is part of the request params, not a post-fetch
    // client sort that a refresh could wipe).
  }, [page, search, sort, stageFilter, continuingView, telehealthOnly, showClosedContinuing, locationFilter, pickedOnly]);

  useEffect(() => { fetchPatients(); }, [fetchPatients]);

  // Auto-sync on first load if no patients (only once)
  const hasSyncedRef = useRef(false);
  useEffect(() => {
    if (!loading && total === 0 && !hasSyncedRef.current) {
      hasSyncedRef.current = true;
      handleSync();
    }
  }, [loading]); // eslint-disable-line

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await api.post("/crm/sync");
      showToast(`Synced ${res.data.synced} patients (${res.data.created} new, ${res.data.updated} updated)`);
      fetchPatients();
    } catch (err) {
      showToast(formatApiError(err, "Sync failed"), "error");
    } finally {
      setSyncing(false);
    }
  };

  const sessionId = useMemo(() => generateSessionId(), []);

  // Non-null when the direct-click acquire hit a 409 — the modal opens in
  // read-only mode showing who holds the lock.
  const [lockConflict, setLockConflict] = useState(null);

  // Mirrors backend crm_router.LOCK_TTL_MINUTES (do not change independently).
  const LOCK_TTL_MINUTES = 5;

  // Heartbeat: while the modal is open with a lock we hold, re-acquire every
  // TTL/2 (idempotent for own session) so a long call doesn't let the lock
  // expire mid-edit. Skipped in read-only mode (we don't hold the lock).
  // A 409 here means someone (an admin) took the patient over — drop to
  // read-only with the banner instead of silently editing a lock we lost.
  useEffect(() => {
    if (!queueMode || !currentPatient?.id || lockConflict) return;
    const interval = setInterval(() => {
      api.post(`/crm/queue/acquire/${currentPatient.id}?session_id=${sessionId}`)
        .catch((err) => {
          if (err?.response?.status === 409 && currentIdRef.current === currentPatient.id) {
            setLockConflict(err.response.data?.detail || {});
          }
        });
    }, (LOCK_TTL_MINUTES / 2) * 60 * 1000);
    return () => clearInterval(interval);
  }, [queueMode, currentPatient?.id, lockConflict, sessionId]);

  // Admin-only, explicit cross-user takeover (confirmation lives in
  // LockBanner). On success the modal becomes editable immediately.
  const handleTakeover = async () => {
    if (!currentPatient?.id) return;
    try {
      const res = await api.post(`/crm/queue/takeover/${currentPatient.id}?session_id=${sessionId}`);
      if (currentIdRef.current !== currentPatient.id) return; // card moved on: B keeps its own lock state
      setLockConflict(null);
      showToast(
        res.data?.took_over
          ? `Editing taken over from ${res.data.previous_holder}`
          : "Editing unlocked",
        "success",
      );
    } catch (err) {
      showToast("Could not take over editing", "error");
    }
  };

  // Best-effort release on tab close. navigator.sendBeacon can't carry the
  // Bearer header our API requires, so we use fetch(keepalive) — the beacon
  // equivalent that survives unload AND authenticates. The TTL remains the
  // real safety net when this doesn't land.
  const currentPatientRef = useRef(null);
  currentPatientRef.current = queueMode && !lockConflict ? currentPatient : null;
  useEffect(() => {
    const onBeforeUnload = () => {
      const p = currentPatientRef.current;
      if (!p?.id) return;
      const token = sessionStorage.getItem("token") || localStorage.getItem("token");
      try {
        fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/crm/queue/release/${p.id}?session_id=${sessionId}`,
          { method: "POST", keepalive: true, headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
      } catch {
        // best-effort only
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [sessionId]);

  const handleQueueMode = async () => {
    setIsDirectView(false);
    setLockConflict(null);
    setActing(true);
    try {
      const res = await api.get(
        `/crm/queue/next?session_id=${sessionId}&location_filter=${locationFilter}`
      );
      if (!res.data.patient) {
        setQueueEmpty(true);
        setQueueMode(true);
        setCurrentPatient(null);
        setServeReason(null);
        showToast("Queue is empty — no active patients to contact", "info");
      } else {
        setQueueMode(true);
        setQueueEmpty(false);
        setCurrentPatient(res.data.patient);
        setServeReason(res.data.serve_reason || null);
        setServeReasonIsSnapshot(!!res.data.serve_reason_is_snapshot);
      }
    } catch (err) {
      showToast("Failed to get next patient", "error");
    } finally {
      setActing(false);
    }
  };

  const handleBannerAction = async (action, data) => {
    if (!currentPatient) return;
    if (action === "__appointment_booked__") {
      // Fail-hard contract (2026-06): success:false means Raintree rejected
      // the booking and NOTHING was persisted — the ScheduleModal shows the
      // persistent inline error banner and stays open, so this handler only
      // ever sees successful bookings. Defensive branch kept anyway.
      if (data?.success === false) {
        showToast(data?.error || "Booking failed — nothing was saved", "error");
        return;
      }
      const appt = data?.appointment;
      const loc = appt?.location || "";
      // Qualifying action: a booking is a disposition with a real next step.
      setActionSavedForId(String(currentPatient.id));
      if (appt?.orphaned_raintree_appointment_id) {
        // W3 item 5: Raintree didn't cancel the old visit - one warning instead of the success toast.
        sonnerToast.warning(rescheduleOrphanText(data?.old_visit_day), { duration: 15000 });
      } else {
        showToast(`Appointment scheduled at ${loc} · Raintree notified`, "success");
      }
      // Refresh patient data to update the banner with new appointment info
      // but keep the banner open so user can send confirmation SMS
      fetchPatients();
      // Update current patient with the new appointment info
      if (data?.appointment) {
        setCurrentPatient(prev => ({
          ...prev,
          appointment_datetime: data.appointment.appointment_datetime,
          appointment_location: data.appointment.location,
        }));
      }
      return;
    }
    setActing(true);
    try {
      let toStage;
      // Unified Status-dropdown path — both queue-mode AND direct-view
      // route through PUT /patients/{id}/stage so all 11 stages are
      // reachable from the banner. (Old "didnt_answer"/"ready_to_schedule"
      // shortcut buttons are gone.)
      if (action === "__set_stage__") {
        const newStageId = data?.stage_id;
        if (!newStageId) return;
        const stageSaved = await api.put(
          `/crm/patients/${currentPatient.id}/stage`,
          { patient_id: String(currentPatient.id), action: String(newStageId) }
        );
        if (currentIdRef.current !== currentPatient.id) { window.dispatchEvent(new Event("action-queue-refresh")); fetchPatients(); return; } // card moved on: refresh the list only
        // P10 1g (network-confirm-and-close): a call on a card at Needs Patient Confirmation
        // can be logged without moving it; the server's stage wins over the one picked.
        const keptStageId = stageSaved?.data?.current_stage_id;
        if (keptStageId != null && keptStageId !== newStageId) {
          const keptName = (stageModel && stageModel.labelFor(keptStageId)) || ALL_STAGES.find(s => s.id === keptStageId)?.name;
          setActionSavedForId(String(currentPatient.id));
          showToast(`Call logged. Stays at "${keptName}"`);
          setServeReason(null);
          window.dispatchEvent(new Event("action-queue-refresh"));
          setCurrentPatient(prev => prev ? { ...prev, current_stage_id: keptStageId, stage_name: keptName } : prev);
          fetchPatients();
          return;
        }
        toStage = (stageModel && stageModel.labelFor(newStageId)) || ALL_STAGES.find(s => s.id === newStageId)?.name;
        // Qualifying action (Ticket 1): the status/stage save unlocks Next
        // immediately, off this response — no refresh, no re-serve.
        setActionSavedForId(String(currentPatient.id));
        showToast(`Moved to "${toStage}"`);
        // The server cleared this user's queue marker on the save, so the
        // engine's why-served line no longer applies to what is on screen.
        setServeReason(null);
        // Handling a clinic confirm in queue mode also acknowledges the
        // Action Queue item server-side (one truth, two views) — tell the
        // sidebar badge so it stops showing the old count until its poll.
        window.dispatchEvent(new Event("action-queue-refresh"));
        // Update banner pill optimistically so the new color appears
        // before the list refetch lands.
        setCurrentPatient(prev => prev ? { ...prev, current_stage_id: newStageId, stage_name: toStage } : prev);
        fetchPatients();
        return;
      }
      if (isDirectView) {
        // Direct view fallback. The legacy "didnt_answer" /
        // "ready_to_schedule" shortcuts are gone from the UI; all stage
        // changes route through "__set_stage__" above. This branch only
        // fires if a non-stage queue action (e.g. set_callback) bubbles
        // up here unexpectedly — leave it as a no-op so we don't 500.
        return;
      } else {
        const res = await api.post("/crm/queue/action", { patient_id: String(currentPatient.id), action, session_id: sessionId });
        toStage = (stageModel && stageModel.labelFor(res.data.to_stage)) || ALL_STAGES.find(s => s.id === res.data.to_stage)?.name;
      }
      showToast(action === "didnt_answer" ? `Moved to "${toStage}"` : `Marked as "${toStage}"`);
      setCurrentPatient(null);
      setQueueMode(false);
      setIsDirectView(false);
      setLockConflict(null);
      setServeReason(null);
      window.dispatchEvent(new Event("action-queue-refresh")); // sidebar badge
      fetchPatients();
    } catch (err) {
      showToast(formatApiError(err, "Action failed"), "error");
    } finally {
      setActing(false);
    }
  };

  const handleRelease = async () => {
    // No !isDirectView gate: calling /queue/release on an unlocked patient is a
    // safe no-op server-side, and a direct-view session may have acquired a real
    // lock via a prior Next click.
    if (currentPatient) {
      try {
        await api.post(`/crm/queue/release/${currentPatient.id}?session_id=${sessionId}`);
      } catch (err) {
        console.error("Failed to release patient lock:", err);
      }
    }
    setCurrentPatient(null);
    setQueueMode(false);
    setIsDirectView(false);
    setLockConflict(null);
    // The lock is gone — refresh so the row stops saying "Locked by ...".
    fetchPatients();
  };

  // Ticket 1 (2026-09-05): `handleSkip` and its `/crm/queue/skip` call were
  // removed from the page — queue mode has no skip affordance. The endpoint,
  // the `crm_queue_skips` table and every historical row are untouched, so
  // reporting and a future policy change both still have their machinery.

  // Move to next patient in queue (after completing current patient)
  const handleNextPatient = async () => {
    // Release current patient lock first.
    // We do NOT gate on !isDirectView here: if the previous patient was acquired
    // via /queue/next (which is always the case after the first Next click in any
    // session, even one started via row click), they hold a real lock that must
    // be released. Calling /queue/release on a patient with no active lock is
    // safe — the backend's can_release check no-ops if there's nothing to release.
    if (currentPatient) {
      try {
        await api.post(`/crm/queue/release/${currentPatient.id}?session_id=${sessionId}`);
      } catch (err) {
        console.error("Failed to release patient lock:", err);
      }
    }
    // Get next patient in queue
    setActing(true);
    setLockConflict(null);
    try {
      // Pass the just-released patient's ID so the backend excludes them from
      // this one query — otherwise the now-unlocked patient is still the
      // highest-priority candidate and gets re-served immediately, and the
      // queue never advances.
      const excludeId = currentPatient?.id;
      const res = await api.get(
        `/crm/queue/next?session_id=${sessionId}&location_filter=${locationFilter}` +
        (excludeId ? `&exclude_patient_id=${excludeId}` : "")
      );
      if (!res.data.patient) {
        setQueueEmpty(true);
        setCurrentPatient(null);
        setServeReason(null);
        showToast("Queue complete — no more patients to contact", "info");
      } else {
        setQueueEmpty(false);
        setCurrentPatient(res.data.patient);
        setServeReason(res.data.serve_reason || null);
        setServeReasonIsSnapshot(!!res.data.serve_reason_is_snapshot);
        // After /queue/next succeeds, we have a real queue-acquired lock. Any
        // prior "direct view" state is no longer accurate — clear it so future
        // Next/Release clicks behave as normal queue mode.
        setIsDirectView(false);
        showToast(`Now working with ${res.data.patient.full_name || "next patient"}`);
      }
    } catch (err) {
      showToast("Failed to get next patient", "error");
    } finally {
      setActing(false);
      fetchPatients(); // Refresh list
    }
  };

  // Click a row to view patient details directly. Acquire the shared patient
  // lock first (same columns/TTL as Queue Mode). 409 → open read-only with a
  // "being worked by" banner — viewing stays allowed.
  const handlePatientRowClick = async (patient) => {
    let conflict = null;
    try {
      await api.post(`/crm/queue/acquire/${patient.id}?session_id=${sessionId}`);
    } catch (err) {
      if (err?.response?.status === 409) {
        conflict = err.response.data?.detail || {};
      }
      // Non-409 acquire failures (network blip etc.) open normally — the
      // TTL machinery stays the safety net.
    }
    setLockConflict(conflict);
    setCurrentPatient(patient);
    setQueueMode(true);
    setQueueEmpty(false);
    setIsDirectView(true);
    // Nothing was served by the engine here — no reason to show.
    setServeReason(null);
  };

  // Manual stage change from list
  const handleManualStage = async (patientId, newStageId) => {
    try {
      await api.put(`/crm/patients/${patientId}/stage`, { patient_id: String(patientId), action: String(newStageId) });
      showToast("Stage updated");
      fetchPatients();
    } catch (err) {
      showToast(formatApiError(err, "Update failed"), "error");
    }
  };

  // ── Continuing view split (server-side since 2026-06) ────────────────────
  // The server is the filter: list_patients applies stage_id + continuing +
  // sort + pagination together, so the rows here are a display-only
  // pass-through. Counts come from the /stage-counts whole-population
  // aggregate — never derived from the loaded page.
  const visibleRows = patients;
  const viewStageCounts = serverCounts.regular || {};
  const continuingCount = serverCounts.continuing_total || 0;
  // Item 9 — how many closed Continuing cards the toggle would reveal.
  const continuingClosedCount = serverCounts.continuing_closed || 0;

  const activeCount = [1, 2, 3, 4, 6, 7].reduce((s, id) => s + (viewStageCounts[id] || 0), 0);
  const closedCount = [8, 9, 10, 11, 12].reduce((s, id) => s + (viewStageCounts[id] || 0), 0);

  return (
    <StageModelContext.Provider value={stageModel}>
    <div className="flex-1 overflow-auto">
      {/* Header */}
      <div className="sticky top-0 z-10 flex h-16 items-center justify-between gap-4 border-b border-slate-200 bg-white/80 px-8 backdrop-blur-md">
        <div>
          <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            CRM Queue
          </h1>
          <p className="text-xs text-slate-400">{activeCount} active · {closedCount} closed · {total} total</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 text-xs text-slate-500 bg-slate-100 px-3 py-1.5 rounded-lg">
            <RefreshCw size={13} className="text-slate-400" />
            Auto-syncs every 5 sec
          </div>
          <button
            data-testid="queue-mode-btn"
            onClick={handleQueueMode}
            disabled={acting}
            className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm"
          >
            <PlayCircle size={16} />
            {acting ? "Loading…" : "Queue Mode"}
          </button>
        </div>
      </div>

      {/* Stage stat strip — active stages on the left, the 4 terminal
          "Closed" stages live behind a single collapsible group on the
          right so they don't drown out the calling-funnel pills. */}
      <StageStrip
        stageCounts={viewStageCounts}
        stageFilter={stageFilter}
        setStageFilter={(v) => { setStageFilter(v); setContinuingView(false); setPage(1); }}
        continuingView={continuingView}
        setContinuingView={(on) => { setContinuingView(on); setStageFilter(""); setPage(1); }}
        continuingCount={continuingCount}
        continuingClosedCount={continuingClosedCount}
        showClosedContinuing={showClosedContinuing}
        setShowClosedContinuing={setShowClosedContinuing}
      />

      {/* Search bar */}
      <div className="px-8 py-3 border-b border-slate-100 bg-white flex items-center justify-between gap-4">
        <div className="relative max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="crm-search"
            type="text"
            placeholder="Search name, phone, DOB, patient #, claim #…"
            value={search}
            onChange={(e) => {
              clearTimeout(searchTimer.current);
              const v = e.target.value;
              setSearch(v);
              // 2026-06-11: reset to page 1 whenever the result set is
              // re-filtered. Without this, typing a search from page 2+
              // sends {page: 2, search: "..."}, which is "page 2 of the
              // filtered results" — almost always empty, so the UI
              // showed "no one exists" even for patients that match.
              setPage(1);
            }}
            className="w-full pl-8 pr-4 h-9 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
          />
        </div>
        {/* WestStar | Network | Both master filter (2026-06, 8/6 meeting).
            Both = default = exactly today's behavior. */}
        <div
          className="flex items-center rounded-lg border border-slate-200 overflow-hidden"
          data-testid="location-master-filter"
        >
          {LOCATION_FILTER_OPTIONS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              data-testid={`location-filter-${key}`}
              onClick={() => { setLocationFilter(key); setPage(1); }}
              className={`px-3 h-9 text-xs font-semibold transition-colors ${
                locationFilter === key
                  ? "bg-blue-600 text-white"
                  : "bg-white text-slate-600 hover:bg-slate-50"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        {/* PR #9 (2026-10-05): patients who picked times on the T-01 link. */}
        <button
          type="button"
          data-testid="patient-picked-filter"
          aria-pressed={pickedOnly}
          onClick={() => { setPickedOnly((v) => !v); setPage(1); }}
          className={`px-3 h-9 text-xs font-semibold rounded-lg border transition-colors ${
            pickedOnly ? "bg-sky-600 text-white border-sky-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
          }`}
        >
          Patient picked times
        </button>
        {/* Telehealth checkbox (2026-10-07): the telehealth team's daily list:
            ticked the box, no in-person visit within 3 business days. */}
        <button
          type="button"
          data-testid="crm-filter-telehealth"
          aria-pressed={telehealthOnly}
          onClick={() => { setTelehealthOnly((v) => !v); setPage(1); }}
          className={`px-3 h-9 text-xs font-semibold rounded-lg border transition-colors ${
            telehealthOnly ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
          }`}
        >
          Telehealth list
        </button>
        {/* Pagination — moved here from below the patient table on 2026-06-05
            so schedulers don't have to scroll to paginate. Same logic and
            handlers, just relocated to the top toolbar, right-aligned. */}
        {total > 50 && (
          <div className="flex items-center gap-3 ml-auto" data-testid="crm-pagination">
            <p className="text-xs text-slate-400">Page {page} · {total} total patients</p>
            <div className="flex gap-2">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                className="px-3 py-1.5 text-xs border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-40">Prev</button>
              <button onClick={() => setPage((p) => p + 1)} disabled={patients.length < 200}
                className="px-3 py-1.5 text-xs border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-40">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Patient table */}
      <div className="p-8">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-slate-400 text-sm">Loading patients…</div>
          ) : patients.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-slate-500 font-medium mb-2">No patients in CRM Queue</p>
              <p className="text-slate-400 text-sm mb-4">Patients appear here after completing Referral Intake</p>
              <p className="text-slate-400 text-xs">Auto-syncs from BigQuery every 5 seconds</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="crm-patients-table">
                <thead className="border-b border-slate-200 bg-slate-50">
                  {/* Every header sorts SERVER-SIDE through one mechanism
                      (SORT_COLUMNS → sort_by/sort_dir → the endpoint's
                      whitelist). Only one sort can be active; the third
                      click restores the queue's default priority order. */}
                  <tr>
                    {SORT_COLUMNS.map((col) => (
                      <SortableTh
                        key={col.key}
                        col={col}
                        sort={sort}
                        onSort={(next) => { setSort(next); setPage(1); }}
                      />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((p) => (
                    <tr
                      key={p.id}
                      data-testid={`crm-row-${p.id}`}
                      onClick={() => handlePatientRowClick(p)}
                      className={`border-b border-slate-100 hover:bg-blue-50/40 transition-colors cursor-pointer ${p.is_locked ? "bg-amber-50/40" : ""}`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="font-medium text-slate-900">{p.full_name || "—"}</div>
                          <span
                            className="text-[11px] text-slate-500 font-mono whitespace-nowrap"
                            data-testid={`crm-row-dob-${p.id}`}
                          >
                            DOB: {formatDOB(p.date_of_birth)}
                          </span>
                          <LocationClassBadge cls={p.queue_bucket || p.location_class} testId={`crm-row-locclass-${p.id}`} />
                          {/* Item 8 (2026-09-20) — post-op chip on the
                              queue row. Display only here; ordering is
                              Commit C. */}
                          {p.post_op && (
                            <span
                              data-testid={`crm-row-post-op-${p.id}`}
                              title={p.post_op_source ? `Post-op (source: ${p.post_op_source})` : "Post-op"}
                              className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-violet-100 text-violet-800 border border-violet-200 whitespace-nowrap"
                            >
                              Post-op
                            </span>
                          )}
                          {/* PR #9 (2026-10-05): picked times on the T-01 link. */}
                          {p.patient_pick && (
                            <span
                              data-testid={`crm-row-patient-pick-${p.id}`}
                              title={`Patient picked times${pickClinicLabel(p.patient_pick) ? ` · ${pickClinicLabel(p.patient_pick)}` : ""}`}
                              className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-sky-100 text-sky-800 border border-sky-200 whitespace-nowrap"
                            >
                              Picked times
                            </span>
                          )}
                          {p.telehealth?.on_list && (
                            <span
                              data-testid={`crm-row-telehealth-${p.id}`}
                              title="Book a telehealth evaluation (THEVA) at the Telehealth location (00020) within 1 business day"
                              className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-violet-100 text-violet-800 border border-violet-200 whitespace-nowrap"
                            >
                              Telehealth OK
                            </span>
                          )}
                          {/* 2026-10-20 (esign_tracking): paperwork complete, or who it waits on. */}
                          <EsignChip summary={p.esign} testId={`crm-row-esign-${p.id}`} />
                        </div>
                        {p.employer_name && <div className="text-xs text-slate-400 truncate max-w-32">{p.employer_name}</div>}
                        {p.is_locked && (
                          <span className="text-xs text-amber-600 font-medium">Locked by {p.locked_by_username}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {p.phone ? (
                          <a href={`tel:${p.phone}`} className="text-blue-600 hover:text-blue-800 font-mono text-xs hover:underline">
                            {p.phone}
                          </a>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600 max-w-40">
                        {assignedClinicLabel(p) ? (
                          <span
                            className="truncate block"
                            title={assignedClinicLabel(p)}
                            data-testid={`assigned-clinic-cell-${p.id}`}
                          >
                            {assignedClinicLabel(p)}
                          </span>
                        ) : (
                          <span className="text-slate-300" data-testid={`assigned-clinic-cell-${p.id}`}>—</span>
                        )}
                      </td>
                      <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                        <StageDropdown
                          currentStageId={p.current_stage_id}
                          onSelect={(sid) => handleManualStage(p.id, sid)}
                          patientId={p.id}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <IntakeTypeBadge type={p.intake_type} />
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-medium ${p.hours_in_stage > 48 ? "text-red-600" : p.hours_in_stage > 24 ? "text-amber-600" : "text-slate-500"}`}>
                          {formatTime(p.hours_in_stage)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs whitespace-nowrap">
                        {/* Item 7: same COALESCE rule as the card, and the
                            same refusal to print "N/A". */}
                        {injuryDateValue(p) ? (
                          <span className="text-slate-600" data-testid={`crm-row-doi-${p.id}`}>
                            {formatDateOnly(injuryDateValue(p), { nullLabel: "missing" })}
                          </span>
                        ) : (
                          <span className="text-amber-700 font-semibold" data-testid={`crm-row-doi-${p.id}`}>
                            missing
                          </span>
                        )}
                      </td>
                      {/* Initial Eval — the appointment mirror, rendered
                          through the house wall-clock helper (the stamp
                          carries a Pacific clock face, never converted).
                          A past date on an undisposed patient is real and
                          deliberately not suppressed. */}
                      <td
                        className="px-4 py-3 text-xs whitespace-nowrap"
                        data-testid={`initial-eval-cell-${p.id}`}
                        title={p.appointment_datetime ? formatWallClockDateTime(p.appointment_datetime) : ""}
                      >
                        {p.appointment_datetime ? (
                          <span className="text-slate-600">{formatWallClockDateTime(p.appointment_datetime)}</span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3" data-testid={`icd10-cell-${p.id}`}>
                        <Icd10Compact codes={p.icd10_codes} review={p.icd10_review} />
                      </td>
                      <td
                        className="px-4 py-3 text-xs text-slate-400 whitespace-nowrap"
                        data-testid="date-added-cell"
                        title={p.created_at ? new Date(p.created_at).toLocaleString() : ""}
                      >
                        {p.created_at
                          ? new Date(p.created_at).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })
                          : "—"}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600">{p.therapy_type || "—"}</td>
                      <td className="px-4 py-3 text-xs text-slate-600 max-w-32 truncate" title={p.insurance_carrier}>{p.insurance_carrier || "—"}</td>
                      <td className="px-4 py-3 text-xs max-w-40">
                        {p.primary_physician_name ? (
                          <div>
                            <div className="font-medium text-teal-700 truncate" title={p.primary_physician_name}>
                              {p.primary_physician_name}
                            </div>
                            {p.primary_physician_phone && (
                              <a href={`tel:${p.primary_physician_phone}`} className="text-teal-500 hover:text-teal-700 font-mono text-[10px] hover:underline">
                                {p.primary_physician_phone}
                              </a>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 max-w-[180px]">
                        {p.notes ? (
                          <p className="text-xs text-slate-500 italic truncate" title={p.notes}>{p.notes}</p>
                        ) : (
                          <span className="text-slate-300 text-xs">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Queue Banner */}
      {queueMode && currentPatient && (
        <QueueBanner
          key={`queue-card-${currentPatient.id}`}
          patient={currentPatient}
          onAction={handleBannerAction}
          onRelease={handleRelease}
          onNext={handleNextPatient}
          onActionRecorded={() => setActionSavedForId(String(currentPatient.id))}
          actionSaved={actionSavedForId === String(currentPatient.id)}
          serveReason={serveReason}
          serveReasonIsSnapshot={serveReasonIsSnapshot}
          acting={acting}
          locations={locations}
          isDirectView={isDirectView}
          readOnly={!!lockConflict}
          lockedBy={lockConflict?.locked_by_username}
          lockInfo={lockConflict}
          onTakeover={handleTakeover}
          onNotesSaved={(updatedNotes) => {
            // Refresh table to show updated notes
            setPatients(prev => prev.map(p => p.id === currentPatient.id ? { ...p, notes: updatedNotes } : p));
          }}
          onPatientUpdated={(patientId, updates) => {
            // Refresh table to show updated fields
            setPatients(prev => prev.map(p => p.id === patientId ? { ...p, ...updates } : p));
            // Also update currentPatient
            setCurrentPatient(prev => prev?.id === patientId ? { ...prev, ...updates } : prev);
          }}
        />
      )}

      {/* Queue empty message */}
      {queueMode && queueEmpty && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: "rgba(15,23,42,0.55)" }}>
          <div className="bg-white rounded-2xl p-8 shadow-2xl text-center max-w-sm">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <PlayCircle size={32} className="text-emerald-600" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Queue Complete</h3>
            <p className="text-slate-500 text-sm mb-6">All active patients have been contacted or are locked by another rep.</p>
            <button onClick={() => setQueueMode(false)} className="px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors">
              Close
            </button>
          </div>
        </div>
      )}

      <Toast message={toast.message} type={toast.type} />
    </div>
    </StageModelContext.Provider>
  );
}

// ── Stage dropdown (a.k.a. Status dropdown) ──────────────────────────────────
// The patient's current stage shown as a styled colored pill that doubles
// as a dropdown trigger. Clicking opens the same Active / Closed split as
// the table-row Move popover. Closed-stage selections prompt for
// confirmation (they're terminal). `size` toggles the row vs detail-panel
// look. Width is auto so it fits in tight table cells.
function StageDropdown({ currentStageId, onSelect, patientId, size = "sm" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const v2 = useStageModel(); // CRM statuses v2: null with STATUS_NAMES_V2 off
  const current = ALL_STAGES.find((s) => s.id === currentStageId);
  const s = v2 ? v2.styleFor(currentStageId) : (STAGE_STYLES[currentStageId] || STAGE_STYLES[2]);
  const isLg = size === "lg";

  const handlePick = (nextId) => {
    setOpen(false);
    if (nextId === currentStageId) return;
    if (v2 ? v2.isClosed(nextId) : CLOSED_STAGE_IDS.includes(nextId)) {
      const nextName = (v2 ? v2.labelFor(nextId) : ALL_STAGES.find((x) => x.id === nextId)?.name) || "this closed stage";
      const ok = window.confirm(
        `Move this patient to "${nextName}"?\n\nClosed stages are terminal — the patient will no longer appear in the active queue.`
      );
      if (!ok) return;
    }
    onSelect(nextId);
  };

  const renderOption = (stage) => {
    const ss = v2 ? v2.styleFor(stage.id) : (STAGE_STYLES[stage.id] || STAGE_STYLES[2]);
    const isClosed = v2 ? false : CLOSED_STAGE_IDS.includes(stage.id); // v2 names carry no prefix
    return (
      <button
        key={stage.id}
        type="button"
        data-testid={`stage-option-${stage.id}`}
        onClick={() => handlePick(stage.id)}
        className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs hover:bg-slate-50 transition-colors ${
          stage.id === currentStageId ? "font-semibold bg-slate-50" : ""
        }`}
      >
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${ss.dot}`} />
        <span className="text-slate-700 text-left flex-1">
          {isClosed ? stage.name.replace(/^Closed:\s*/, "") : stage.name}
        </span>
        {stage.id === currentStageId && <span className="ml-auto text-blue-600">✓</span>}
      </button>
    );
  };

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        data-testid={`stage-dropdown-${patientId}`}
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 rounded-full font-medium border whitespace-nowrap transition hover:brightness-95 hover:shadow-sm ${s.bg} ${s.text} border-transparent ${
          isLg ? "px-3 py-1.5 text-sm" : "px-2.5 py-1 text-xs"
        }`}
        title="Click to change status"
      >
        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${s.dot}`} />
        <span className="truncate max-w-[180px]">
          {v2 ? v2.labelFor(currentStageId, current?.name || "Set status") : current?.name ? (CLOSED_STAGE_IDS.includes(currentStageId) ? current.name.replace(/^Closed:\s*/, "Closed · ") : current.name) : "Set status"}
        </span>
        <ChevronDown size={isLg ? 13 : 11} className="opacity-60" />
      </button>
      {open && (
        <div
          data-testid={`stage-dropdown-menu-${patientId}`}
          className="absolute right-0 top-full mt-1 w-60 bg-white border border-slate-200 rounded-xl shadow-xl z-30 py-1"
        >
          {v2 ? v2.groups.map((g) => (
            // CRM statuses v2: grouped by tab; dark statuses (17-24) are not
            // offered — the server refuses them too.
            <div key={g.key}>
              <div className="px-3 pt-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{g.label}</div>
              {g.stages.filter((st) => st.available || st.stage_id === currentStageId)
                .map((st) => renderOption({ id: st.stage_id, name: st.display_v2 }))}
            </div>
          )) : (<>
          <div className="px-3 pt-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            Active
          </div>
          {ALL_STAGES.filter((stage) => !CLOSED_STAGE_IDS.includes(stage.id)).map(renderOption)}
          <div className="my-1 mx-3 border-t border-slate-100" />
          <div className="px-3 pt-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            Closed (Terminal)
          </div>
          {ALL_STAGES.filter((stage) => CLOSED_STAGE_IDS.includes(stage.id)).map(renderOption)}
          </>)}
        </div>
      )}
    </div>
  );
}
